import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Token from './Token';
import ContextMenu from './ContextMenu';
import UserNav from './UserNav';
import SummaryMenu from './SummaryMenu';
import SpeakerLabel from './SpeakerLabel';
import TimestampEditor from './TimestampEditor';
import TimestampShiftDialog from './TimestampShiftDialog';
import TimestampDistributeDialog from './TimestampDistributeDialog';
import BoundaryDragHandle from './BoundaryDragHandle';
import CorrectionRequest from './CorrectionRequest';
import { useAuth } from '../hooks/useAuth';
import { debugLog } from '../utils/debug';
import {
  applyTimestampWithRipple,
  shiftTimestamps,
  distributeTimestamps,
  adjustBoundary,
  syncFlatWordsArray,
  updateSegmentBoundaries
} from '../utils/timestampUtils';
import './Transcript.css';
const FILLER_WORDS = new Set([
  'um', 'uh', 'like', 'you know', 'i mean', 'sort of', 'kind of',
  'actually', 'basically', 'literally', 'right', 'so', 'well',
  'okay', 'oh', 'ah', 'er', 'hmm', 'mhm'
]);


const Transcript = ({ transcriptId, data, audioRef, onTokenClick, onTranscriptChange, onDownload, onDownloadTxt, onCopy, onShare, onGenerateSummary, onGenerateName, onRetryTranscription, isTranscribing, currentTime, highThreshold, mediumThreshold, onHighThresholdChange, onMediumThresholdChange, typingMode, onTypingModeToggle, showConfidence, onShowConfidenceToggle, showSegments, onShowSegmentsToggle, segmentDuration = 10, onSegmentDurationChange, hideGhosted: propHideGhosted, onHideGhostedToggle, clickToDeleteMode: propClickToDeleteMode, onClickToDeleteModeToggle, suggestionsMode, onSuggestionsModeToggle, transcriptName, onTranscriptNameChange, isSettingsVisible: propIsSettingsVisible, setIsSettingsVisible: propSetIsSettingsVisible, isHelpVisible: propIsHelpVisible, setIsHelpVisible: propSetIsHelpVisible, showConfidenceLegend: propShowConfidenceLegend, onShowConfidenceLegendToggle, enableBinNavigation: propEnableBinNavigation, onEnableBinNavigationToggle, showSuggestionsModeToggle: propShowSuggestionsModeToggle, onShowSuggestionsModeToggleChange, showClickToDeleteToggle: propShowClickToDeleteToggle, onShowClickToDeleteToggleChange, enableDiarization: propEnableDiarization, onEnableDiarizationToggle, readOnlyAlternatives = false }) => {
  const { user } = useAuth();
  const transcriptContentRef = useRef(null);
  const wordRefs = useRef([]);
  const suggestionRefs = useRef([]); // Track suggestion option elements for scroll-into-view
  const dataRef = useRef(data);
  const autoPauseCleanupRef = useRef(null); // Track current auto-pause listeners for cleanup
  const [selectedWordIndex, setSelectedWordIndex] = useState(null);
  const [draggedWord, setDraggedWord] = useState(null);
  const [internalClickToDeleteMode, setInternalClickToDeleteMode] = useState(false);
  const [internalHideGhosted, setInternalHideGhosted] = useState(false);
  const [internalIsHelpVisible, setInternalIsHelpVisible] = useState(false);
  const [internalIsSettingsVisible, setInternalIsSettingsVisible] = useState(false);
  const [openAlternativesId, setOpenAlternativesId] = useState(null);
  const openAlternativesIdRef = useRef(null);
  const alternativesOpenTimeRef = useRef(0);
  const [internalShowSuggestionsModeToggle, setInternalShowSuggestionsModeToggle] = useState(true);
  const [internalShowConfidenceLegend, setInternalShowConfidenceLegend] = useState(true);
  const [internalEnableBinNavigation, setInternalEnableBinNavigation] = useState(false);
  const [internalShowClickToDeleteToggle, setInternalShowClickToDeleteToggle] = useState(false); // Controls visibility of click-to-delete toggle button (default hidden)
  const [internalEnableDiarization, setInternalEnableDiarization] = useState(false); // Controls speaker diarization (default off)
  const [showTypingModeToggle, setShowTypingModeToggle] = useState(false); // Controls visibility of typing mode toggle button (default hidden)
  const [showSummaryMenu, setShowSummaryMenu] = useState(false);
  const [typingModeEntryMethod, setTypingModeEntryMethod] = useState(null); // Track how typing mode was entered: 'button', 'double-click', 'context-menu'
  const [hideFillerWords, setHideFillerWords] = useState(false); // Controls whether filler words are hidden
  const [internalWhitespaceMode, setInternalWhitespaceMode] = useState(false); // Controls whether whitespace tokens are preserved separately
  const [silenceCompressionMode, setSilenceCompressionMode] = useState(false); // Skip silences longer than 0.25s during playback
  const [correctionsPlaybackMode, setCorrectionsPlaybackMode] = useState(false); // Play only corrected/edited words
  const lastSeekTimeRef = useRef(0); // Prevent rapid seeking in playback modes

  // Speaker management state
  const [transcriptSpeakers, setTranscriptSpeakers] = useState([]);
  const [availableSpeakers, setAvailableSpeakers] = useState([]);

  // Use prop values if provided, otherwise use internal state
  const showSuggestionsModeToggle = propShowSuggestionsModeToggle ?? internalShowSuggestionsModeToggle;
  const showClickToDeleteToggle = propShowClickToDeleteToggle ?? internalShowClickToDeleteToggle;
  const showConfidenceLegend = propShowConfidenceLegend ?? internalShowConfidenceLegend;
  const enableBinNavigation = propEnableBinNavigation ?? internalEnableBinNavigation;
  const enableDiarization = propEnableDiarization ?? internalEnableDiarization;

  // Helper function to check if a word is a filler word
  const isFillerWord = (word) => {
    //const fillerWords = ['um', 'uh', 'like', 'you know', 'i mean', 'sort of', 'kind of', 'actually', 'basically', 'literally', 'right', 'so', 'well', 'okay', 'oh', 'ah', 'er', 'hmm', 'mhm'];
    const cleanWord = word.toLowerCase().replace(/[.,!?;:]/g, '').trim();
    return fillerWords.includes(cleanWord);
  };

  // Helper function to assign speaker to a new word based on context
  const assignSpeakerToNewWord = (data, segmentIndex, wordIndex) => {
    const segment = data?.segments?.[segmentIndex];
    if (!segment || !segment.words) return null;

    const words = segment.words;
    const prevWord = words[wordIndex - 1];
    const nextWord = words[wordIndex + 1];

    // If both neighbors have the same speaker, use that
    if (prevWord?.speaker && nextWord?.speaker && prevWord.speaker === nextWord.speaker) {
      return prevWord.speaker;
    }

    // If only previous word has speaker, use that
    if (prevWord?.speaker) {
      return prevWord.speaker;
    }

    // If only next word has speaker, use that
    if (nextWord?.speaker) {
      return nextWord.speaker;
    }

    // Otherwise, use segment's speaker if available
    return segment.speaker || null;
  };

  // Sync dataRef with data prop to avoid stale closures
  useEffect(() => {
    debugLog('[Transcript] Data prop changed:', {
      hasData: !!data,
      dataIsNull: data === null,
      dataIsUndefined: data === undefined,
      segmentCount: data?.segments?.length || 0,
      timestamp: new Date().toISOString()
    });
    dataRef.current = data;
  }, [data]);

  // Sync ref with state and track when opened
  useEffect(() => {
    openAlternativesIdRef.current = openAlternativesId;
    if (openAlternativesId) {
      alternativesOpenTimeRef.current = Date.now();
    }
  }, [openAlternativesId]);

  // Fetch transcript speakers (requires authentication)
  const fetchTranscriptSpeakers = useCallback(async () => {
    if (!transcriptId) return;
    // Skip for unauthenticated users - speaker features require login
    if (!localStorage.getItem('authToken')) return;

    try {
      debugLog('[TRANSCRIPT] Fetching transcript speakers for:', transcriptId);
      // Include embeddings for speaker matching functionality
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers?includeEmbeddings=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        debugLog('[TRANSCRIPT] Received transcript speakers response:', data);
        // API returns { speakers: [...] }, extract the array
        const speakers = data.speakers || data;
        debugLog('[TRANSCRIPT] Setting transcriptSpeakers to:', speakers);
        setTranscriptSpeakers(speakers);
      } else {
        console.error('[TRANSCRIPT] Failed to fetch transcript speakers:', response.status);
      }
    } catch (err) {
      console.error('Error fetching transcript speakers:', err);
    }
  }, [transcriptId]);

  // Check if this transcript has an active correction request
  React.useEffect(() => {
    if (!transcriptId || !localStorage.getItem('authToken')) return;
    const checkCorrection = async () => {
      try {
        const response = await fetch('/api/corrections/my-requests', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
        });
        if (response.ok) {
          const { requests } = await response.json();
          const hasActive = requests.some(r =>
            r.transcript_id === transcriptId &&
            ['pending', 'assigned', 'in_progress'].includes(r.status)
          );
          setHasPendingCorrection(hasActive);
        }
      } catch (err) {
        console.error('Error checking correction status:', err);
      }
    };
    checkCorrection();
  }, [transcriptId]);

  // Fetch available speakers (requires authentication)
  const fetchAvailableSpeakers = useCallback(async () => {
    // Skip for unauthenticated users - speaker features require login
    if (!localStorage.getItem('authToken')) return;

    try {
      const response = await fetch('/api/speakers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        debugLog('[TRANSCRIPT] Received available speakers:', data);
        // API returns { speakers: [...] }, extract the array
        setAvailableSpeakers(data.speakers || []);
      } else {
        console.error('[TRANSCRIPT] Failed to fetch available speakers:', response.status);
      }
    } catch (err) {
      console.error('Error fetching available speakers:', err);
    }
  }, []);

  // Handler for when speaker is updated
  const handleSpeakerUpdated = useCallback(() => {
    debugLog('[TRANSCRIPT] handleSpeakerUpdated callback triggered, refreshing speaker data');
    fetchTranscriptSpeakers();
    fetchAvailableSpeakers();
  }, [fetchTranscriptSpeakers, fetchAvailableSpeakers]);

  // Fetch speakers on mount
  useEffect(() => {
    fetchTranscriptSpeakers();
    fetchAvailableSpeakers();
  }, [fetchTranscriptSpeakers, fetchAvailableSpeakers]);

  const clickToDeleteMode = propClickToDeleteMode ?? internalClickToDeleteMode;
  const hideGhosted = propHideGhosted ?? internalHideGhosted;
  const isHelpVisible = propIsHelpVisible ?? internalIsHelpVisible;
  const setIsHelpVisible = propSetIsHelpVisible ?? setInternalIsHelpVisible;
  const isSettingsVisible = propIsSettingsVisible ?? internalIsSettingsVisible;
  const setIsSettingsVisible = propSetIsSettingsVisible ?? setInternalIsSettingsVisible;

  // Debug clickToDeleteMode changes
  useEffect(() => {
    debugLog('[Transcript] clickToDeleteMode changed:', clickToDeleteMode, 'prop:', propClickToDeleteMode, 'internal:', internalClickToDeleteMode);
  }, [clickToDeleteMode, propClickToDeleteMode, internalClickToDeleteMode]);

  // Silence compression and corrections playback modes
  useEffect(() => {
    if (!audioRef?.current || !data?.segments || (!silenceCompressionMode && !correctionsPlaybackMode)) {
      return;
    }

    const handleTimeUpdate = () => {
      const audio = audioRef.current;
      if (!audio || audio.paused) return;

      const now = Date.now();
      // Prevent rapid seeking (debounce to max once every 100ms)
      if (now - lastSeekTimeRef.current < 100) return;

      const currentTime = audio.currentTime;

      // Build flat list of all words with their metadata
      const allWords = [];
      data.segments.forEach(segment => {
        segment.words?.forEach(word => {
          allWords.push({
            start: word.start,
            end: word.end,
            word: word.word,
            isGhosted: word.isGhosted,
            isInserted: word.isInserted
          });
        });
      });

      if (allWords.length === 0) return;

      // Find current word and next word
      let currentWordIndex = -1;
      for (let i = 0; i < allWords.length; i++) {
        if (currentTime >= allWords[i].start && currentTime <= allWords[i].end) {
          currentWordIndex = i;
          break;
        }
      }

      // If we're between words, find the next word
      if (currentWordIndex === -1) {
        for (let i = 0; i < allWords.length; i++) {
          if (currentTime < allWords[i].start) {
            currentWordIndex = i - 1;
            break;
          }
        }
      }

      if (correctionsPlaybackMode) {
        // Jump to next corrected word (isGhosted or isInserted)
        const currentWord = allWords[currentWordIndex];

        // If we're currently in a corrected word, let it play
        if (currentWord && (currentWord.isGhosted || currentWord.isInserted)) {
          return; // Stay in corrected word
        }

        // Find next corrected word
        for (let i = currentWordIndex + 1; i < allWords.length; i++) {
          if (allWords[i].isGhosted || allWords[i].isInserted) {
            audio.currentTime = allWords[i].start;
            lastSeekTimeRef.current = now;
            return;
          }
        }

        // No more corrections, pause at end
        audio.pause();
        return;
      }

      if (silenceCompressionMode) {
        // Skip long silences (> 0.25s)
        const nextWordIndex = currentWordIndex + 1;
        if (nextWordIndex < allWords.length) {
          const currentWord = allWords[currentWordIndex];
          const nextWord = allWords[nextWordIndex];

          if (currentWord && currentTime > currentWord.end) {
            const silence = nextWord.start - currentWord.end;
            if (silence > 0.25) {
              // Skip to 0.25s before next word starts
              audio.currentTime = nextWord.start - 0.01;
              lastSeekTimeRef.current = now;
            }
          }
        }
      }
    };

    const audio = audioRef.current;
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [audioRef, data, silenceCompressionMode, correctionsPlaybackMode]);

  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectionStart, setSelectionStart] = useState(null); // Start position of text selection within word
  const [selectionEnd, setSelectionEnd] = useState(null); // End position of text selection within word
  const [multiWordSelection, setMultiWordSelection] = useState(null); // { startWordIndex, endWordIndex, startOffset, endOffset }
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [segmentsPerPage, setSegmentsPerPage] = useState(null); // null = show all segments
  const [currentSegmentPage, setCurrentSegmentPage] = useState(0);

  // Handle Escape key to close popups and return to player mode
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        // Check if any popups are open
        const hasOpenPopups = showSummaryMenu || openAlternativesId || isHelpVisible || isSettingsVisible;

        // First priority: close any open popups
        if (showSummaryMenu) {
          setShowSummaryMenu(false);
          return; // Stop here, don't exit modes
        }
        if (openAlternativesId) {
          setOpenAlternativesId(null);
          return; // Stop here, don't exit modes
        }
        if (isHelpVisible) {
          setIsHelpVisible(false);
          return; // Stop here, don't exit modes
        }
        if (isSettingsVisible) {
          setIsSettingsVisible(false);
          return; // Stop here, don't exit modes
        }

        // Second priority: if no popups are open, exit editing modes
        if (!hasOpenPopups) {
          if (typingMode && onTypingModeToggle) onTypingModeToggle();
          if (clickToDeleteMode && onClickToDeleteModeToggle) onClickToDeleteModeToggle();
          if (suggestionsMode && onSuggestionsModeToggle) onSuggestionsModeToggle();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isHelpVisible, isSettingsVisible, openAlternativesId, showSummaryMenu, typingMode, clickToDeleteMode, suggestionsMode, onTypingModeToggle, onClickToDeleteModeToggle, onSuggestionsModeToggle]);

  // Clear suggestions state and entry method when typing mode is exited
  useEffect(() => {
    if (!typingMode) {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(0);
      setSelectedWordIndex(null);
      setCursorPosition(0);
      setSelectionStart(null);
      setSelectionEnd(null);
      setMultiWordSelection(null);
      setTypingModeEntryMethod(null);
    }
  }, [typingMode]);

  // Reset segment page when data changes or segments per page changes
  useEffect(() => {
    setCurrentSegmentPage(0);
  }, [data, segmentsPerPage]);

  // Detect and track multi-word selection using browser Selection API
  useEffect(() => {
    if (!typingMode || !transcriptContentRef.current) return;

    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setMultiWordSelection(null);
        return;
      }

      const range = selection.getRangeAt(0);
      if (range.collapsed) {
        setMultiWordSelection(null);
        return;
      }

      // Check if selection is within our transcript
      const transcriptEl = transcriptContentRef.current;
      if (!transcriptEl.contains(range.commonAncestorContainer)) {
        return;
      }

      // Find start and end word elements
      let startNode = range.startContainer;
      let endNode = range.endContainer;

      // Navigate up to find the token element
      const findTokenElement = (node) => {
        let current = node;
        while (current && current !== transcriptEl) {
          if (current.nodeType === Node.ELEMENT_NODE && current.classList?.contains('token')) {
            return current;
          }
          current = current.parentNode;
        }
        return null;
      };

      const startToken = findTokenElement(startNode);
      const endToken = findTokenElement(endNode);

      if (!startToken || !endToken) {
        setMultiWordSelection(null);
        return;
      }

      const startGlobalIndex = parseInt(startToken.dataset.globalIndex);
      const endGlobalIndex = parseInt(endToken.dataset.globalIndex);

      // If selecting within a single word, use the word-level selection instead
      if (startGlobalIndex === endGlobalIndex) {
        setMultiWordSelection(null);
        return;
      }

      // Calculate character offsets within the start and end words
      const getCharacterOffset = (token, node, offset) => {
        if (!token) return 0;

        // Find the text node within the token
        const textNode = Array.from(token.childNodes).find(n => n.nodeType === Node.TEXT_NODE);
        if (!textNode) return 0;

        // If the selection node is the text node itself, use the offset directly
        if (node === textNode) {
          return offset;
        }

        // Otherwise, assume selection is at the start or end
        return offset;
      };

      const startOffset = getCharacterOffset(startToken, startNode, range.startOffset);
      const endOffset = getCharacterOffset(endToken, endNode, range.endOffset);

      setMultiWordSelection({
        startWordIndex: Math.min(startGlobalIndex, endGlobalIndex),
        endWordIndex: Math.max(startGlobalIndex, endGlobalIndex),
        startOffset: startGlobalIndex < endGlobalIndex ? startOffset : endOffset,
        endOffset: startGlobalIndex < endGlobalIndex ? endOffset : startOffset
      });
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [typingMode]);

  // Scroll selected suggestion into view when navigating with arrow keys
  useEffect(() => {
    if (showSuggestions && suggestionRefs.current[selectedSuggestionIndex]) {
      suggestionRefs.current[selectedSuggestionIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedSuggestionIndex, showSuggestions]);

  const [copyButtonText, setCopyButtonText] = useState('Copy Text');
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
  const [showCorrectionRequest, setShowCorrectionRequest] = useState(false);
  const [hasPendingCorrection, setHasPendingCorrection] = useState(false);
  const [mobileEditValue, setMobileEditValue] = useState('');
  const mobileInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    selectedWord: null,
    hasSelectedText: false
  });
  const [timestampEditor, setTimestampEditor] = useState(null); // { word, wordIndex, segmentIndex, globalIndex, anchorElement }
  const [timestampShiftDialog, setTimestampShiftDialog] = useState(null); // { selectedWords }
  const [timestampDistributeDialog, setTimestampDistributeDialog] = useState(null); // { selectedWords }
  const isUpdating = useRef(false);
  const pendingDataRef = useRef(null); // Holds in-flight data during rapid typing to prevent lost keystrokes
  const longPressTimer = useRef(null);
  const longPressStartPos = useRef(null);
  const isTypingChange = useRef(false);

  // Process data to split tokens that contain both whitespace and words
  const processedData = useMemo(() => {
    if (!data || typeof data !== 'object') return data;

    // Function to split a word token if it contains both whitespace and non-whitespace
    const splitMixedToken = (word) => {
      const text = word.word || '';

      // If token is purely whitespace or purely non-whitespace, no split needed
      if (!text || text.trim() === '' || text.trim() === text) {
        return [word];
      }

      // Token contains both - split into separate tokens
      const parts = [];
      let currentText = '';
      let isWhitespace = /\s/.test(text[0]);

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const charIsWhitespace = /\s/.test(char);

        if (charIsWhitespace !== isWhitespace) {
          // Transition from word to whitespace or vice versa
          if (currentText) {
            parts.push({
              ...word,
              word: currentText,
              isInserted: word.isInserted || false
            });
          }
          currentText = char;
          isWhitespace = charIsWhitespace;
        } else {
          currentText += char;
        }
      }

      // Add final part
      if (currentText) {
        parts.push({
          ...word,
          word: currentText,
          isInserted: word.isInserted || false
        });
      }

      return parts.length > 0 ? parts : [word];
    };

    // Create a deep copy and process segments
    const newData = JSON.parse(JSON.stringify(data));

    if (newData.segments && Array.isArray(newData.segments)) {
      newData.segments = newData.segments.map(segment => {
        if (segment.words && Array.isArray(segment.words)) {
          const newWords = [];
          segment.words.forEach(word => {
            const splitWords = splitMixedToken(word);
            newWords.push(...splitWords);
          });
          return { ...segment, words: newWords };
        }
        return segment;
      });
    } else if (newData.words && Array.isArray(newData.words)) {
      const newWords = [];
      newData.words.forEach(word => {
        const splitWords = splitMixedToken(word);
        newWords.push(...splitWords);
      });
      newData.words = newWords;
    }

    return newData;
  }, [data]);

  const processedSegments = useMemo(() => {
    if (!processedData || typeof processedData !== 'object') return [];

    // During live recording, use server segments directly without re-grouping.
    // This keeps existing segments stable so only new segments appear at the bottom,
    // giving a natural "line by line" appearance instead of full-transcript flash.
    if (processedData.isLive && processedData.segments && Array.isArray(processedData.segments)) {
      return processedData.segments.map((seg, segIdx) => ({
        start: seg.start ?? 0,
        end: seg.end ?? 0,
        text: seg.text || (seg.words || []).map(w => w.word).join(' '),
        speaker: seg.speaker || null,
        words: (seg.words || []).map((w, wIdx) => ({
          ...w,
          originalSegmentIndex: segIdx,
          originalWordIndex: wIdx,
        })),
      }));
    }

    let allWords = [];

    // Collect all words from processedData, preserving ORIGINAL segment/word indices
    if (processedData.segments && Array.isArray(processedData.segments)) {
      // Flatten all words from all segments, storing original indices
      processedData.segments.forEach((seg, origSegIdx) => {
        if (Array.isArray(seg.words)) {
          seg.words.forEach((word, origWordIdx) => {
            allWords.push({
              ...word,
              originalSegmentIndex: origSegIdx,
              originalWordIndex: origWordIdx
            });
          });
        }
      });
    } else if (processedData.words && Array.isArray(processedData.words)) {
      allWords = processedData.words.map((word, idx) => ({
        ...word,
        originalSegmentIndex: 0,
        originalWordIndex: idx
      }));
    } else if (processedData.text && typeof processedData.text === 'string') {
      allWords = processedData.text.split(/\s+/).filter(Boolean).map((wordText, idx) => ({
        word: wordText, start: null, end: null, confidence: null, isInserted: true,
        originalSegmentIndex: 0,
        originalWordIndex: idx
      }));
    }

    if (allWords.length === 0) {
      return [];
    }

    // Helper to check if a word ends with sentence-ending punctuation
    const isSentenceEnd = (word) => {
      const text = word.word || '';
      return /[.!?]$/.test(text.trim());
    };

    // Group words by time duration, breaking on sentence boundaries when possible
    const groupedSegments = [];
    let currentSegment = {
      words: [],
      start: allWords[0]?.start ?? 0,
      end: allWords[0]?.end ?? 0,
      text: '',
      speaker: null
    };

    for (let i = 0; i < allWords.length; i++) {
      const word = allWords[i];
      const wordStart = word.start ?? currentSegment.start;
      const wordEnd = word.end ?? currentSegment.end;

      // Add word to current segment
      currentSegment.words.push(word);
      if (wordEnd !== null) {
        currentSegment.end = wordEnd;
      }

      // Track speaker - use the first speaker found in the segment, or detect speaker changes
      if (word.speaker) {
        if (currentSegment.speaker === null) {
          currentSegment.speaker = word.speaker;
        } else if (currentSegment.speaker !== word.speaker && currentSegment.words.length > 1) {
          // Speaker changed mid-segment - finalize current segment and start new one
          // Remove the current word from this segment since it belongs to a different speaker
          currentSegment.words.pop();
          currentSegment.text = currentSegment.words.map(w => w.word).join(' ');
          if (currentSegment.words.length > 0) {
            groupedSegments.push(currentSegment);
          }

          // Start new segment with this word
          currentSegment = {
            words: [word],
            start: wordStart,
            end: wordEnd,
            text: '',
            speaker: word.speaker
          };
          continue; // Skip the rest of the loop for this word
        }
      }

      // Check if we should start a new segment
      const duration = wordStart !== null && currentSegment.start !== null
        ? (wordStart - currentSegment.start)
        : 0;

      // If we've reached the minimum duration and this is a sentence end, break here
      if (currentSegment.words.length > 0 &&
          duration >= segmentDuration &&
          isSentenceEnd(word)) {
        // Finalize current segment
        currentSegment.text = currentSegment.words.map(w => w.word).join(' ');
        groupedSegments.push(currentSegment);

        // Start new segment (if there are more words)
        if (i < allWords.length - 1) {
          const nextWord = allWords[i + 1];
          currentSegment = {
            words: [],
            start: nextWord.start ?? wordEnd,
            end: nextWord.end ?? wordEnd,
            text: '',
            speaker: null
          };
        }
      }
      // If duration is significantly over (1.5x) the target, break even without sentence end
      else if (currentSegment.words.length > 0 &&
               duration >= segmentDuration * 1.5) {
        // Finalize current segment
        currentSegment.text = currentSegment.words.map(w => w.word).join(' ');
        groupedSegments.push(currentSegment);

        // Start new segment (if there are more words)
        if (i < allWords.length - 1) {
          const nextWord = allWords[i + 1];
          currentSegment = {
            words: [],
            start: nextWord.start ?? wordEnd,
            end: nextWord.end ?? wordEnd,
            text: '',
            speaker: null
          };
        }
      }
    }

    // Don't forget the last segment
    if (currentSegment.words.length > 0) {
      currentSegment.text = currentSegment.words.map(w => w.word).join(' ');
      groupedSegments.push(currentSegment);
    }

    return groupedSegments;
  }, [processedData, segmentDuration]);

  // Calculate visible segments based on pagination
  const { visibleSegments, totalPages, hasNextPage, hasPrevPage } = useMemo(() => {
    // If segmentsPerPage is null or 0, show all segments
    if (!segmentsPerPage || segmentsPerPage <= 0) {
      return {
        visibleSegments: processedSegments,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      };
    }

    const total = Math.ceil(processedSegments.length / segmentsPerPage);
    const startIndex = currentSegmentPage * segmentsPerPage;
    const endIndex = startIndex + segmentsPerPage;
    const visible = processedSegments.slice(startIndex, endIndex);

    return {
      visibleSegments: visible,
      totalPages: total,
      hasNextPage: currentSegmentPage < total - 1,
      hasPrevPage: currentSegmentPage > 0
    };
  }, [processedSegments, segmentsPerPage, currentSegmentPage]);

  // Close export/AI menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSubtitleMenu && !event.target.closest('.export-ai-menu-container')) {
        setShowSubtitleMenu(false);
      }
    };

    if (showSubtitleMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showSubtitleMenu]);

  // Handle subtitle export
  const handleSubtitleExport = useCallback(async (format) => {
    if (!transcriptId) {
      console.error('No transcript ID for subtitle export');
      return;
    }

    try {
      setShowSubtitleMenu(false);

      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/transcripts/${transcriptId}/export/subtitle?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `transcript.${format}`;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches) {
          filename = matches[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      debugLog(`Subtitle exported as ${format}: ${filename}`);
    } catch (error) {
      console.error('Error exporting subtitle:', error);
    }
  }, [transcriptId]);

  const handleGenerateFormattedVersion = useCallback(async () => {
    if (!transcriptId) {
      console.error('No transcript ID for formatted version');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/transcripts/${transcriptId}/generate-formatted`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Generation failed: ${response.status}`);
      }

      const result = await response.json();

      // Download the formatted text as a file
      const blob = new Blob([result.formattedText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${transcriptName || 'transcript'}-formatted.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      debugLog('Formatted version generated and downloaded');
    } catch (error) {
      console.error('Error generating formatted version:', error);
      alert('Failed to generate formatted version. Please try again.');
    }
  }, [transcriptId, transcriptName]);

  const handleCopy = useCallback(async () => {
    if (!data) return;

    const allSegmentWords = [];
    for (const segment of processedSegments) {
      if (!segment.words) continue;
      const segmentWords = segment.words
        .filter(word => !word.isGhosted && !word.deleted)
        .map(word => word.word);
      allSegmentWords.push(...segmentWords);
    }

    const textContent = allSegmentWords.join(' ');

    try {
      await navigator.clipboard.writeText(textContent);
      setCopyButtonText('Copied!');
      if (onCopy) {
        onCopy(textContent);
      }
    } catch (err) {
      console.error('Could not copy text: ', err);
      setCopyButtonText('Failed!');
    } finally {
      setTimeout(() => setCopyButtonText('Copy Text'), 2000);
    }
  }, [data, processedSegments, onCopy]);

  // Context menu handlers
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();

    // Check if we have selected text
    const selection = window.getSelection();
    const hasSelectedText = selection.toString().trim().length > 0;

    // Try to find the clicked word
    let selectedWord = null;
    const target = e.target;
    if (target.closest('.token')) {
      const tokenElement = target.closest('.token');
      const wordIndex = tokenElement.dataset.globalIndex;
      if (wordIndex !== undefined) {
        selectedWord = {
          index: parseInt(wordIndex),
          element: tokenElement,
          word: null // Will be filled by context menu handlers
        };
      }
    }

    setContextMenu({
      isVisible: true,
      position: { x: e.clientX, y: e.clientY },
      selectedWord,
      hasSelectedText
    });
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Touch event handlers for long-press on mobile
  const longPressTarget = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;

    const touch = e.touches[0];
    longPressStartPos.current = { x: touch.clientX, y: touch.clientY };
    longPressTarget.current = e.target;

    // Set timer for long press (500ms)
    longPressTimer.current = setTimeout(() => {
      // Trigger context menu at touch position
      const syntheticEvent = {
        preventDefault: () => {},
        clientX: touch.clientX,
        clientY: touch.clientY,
        target: longPressTarget.current
      };
      handleContextMenu(syntheticEvent);

      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      // Clear the target
      longPressTarget.current = null;
    }, 500);
  }, [isMobile, handleContextMenu]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !longPressStartPos.current) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - longPressStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - longPressStartPos.current.y);

    // Cancel long press if finger moved more than 10px
    if (deltaX > 10 || deltaY > 10) {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      longPressStartPos.current = null;
    }
  }, [isMobile]);

  const handleTouchEnd = useCallback(() => {
    // Cancel long press timer on touch end
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressStartPos.current = null;
    longPressTarget.current = null;
  }, []);

  const handleContextCopyText = useCallback(async () => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    if (selectedText) {
      try {
        await navigator.clipboard.writeText(selectedText);
      } catch (err) {
        console.error('Failed to copy selected text:', err);
      }
    }
  }, []);

  const handleContextEditWord = useCallback((selectedWord) => {
    if (selectedWord && selectedWord.element) {
      // Enable typing mode if not already enabled
      if (!typingMode && onTypingModeToggle) {
        setTypingModeEntryMethod('context-menu');
        onTypingModeToggle();
      }
      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      setSelectedWordIndex(globalIndex);
    }
  }, [typingMode, onTypingModeToggle]);

  // Define allWords before it's used in callbacks
  const allWords = useMemo(() => {
    debugLog('[Transcript] Processing word data:', {
      hasData: !!data,
      processedSegments: processedSegments.length
    });

    const words = [];
    processedSegments.forEach((segment, segmentIndex) => {
      segment.words.forEach((word, wordIndex) => {
        words.push({
          ...word,
          segmentIndex,
          wordIndex,
          globalIndex: words.length,
        });
      });
    });
    return words;
  }, [processedSegments]);

  // Auto-scroll to bottom during live recording so user always sees latest segments
  useEffect(() => {
    if (data?.isLive && transcriptContentRef.current && processedSegments.length > 0) {
      const container = transcriptContentRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [data?.isLive, processedSegments.length]);

  // Helper function to check if word has valid alternatives
  const hasValidAlternatives = useCallback((word) => {
    if (!word || !word.alternatives || word.alternatives.length === 0 || word.isInserted) {
      return false;
    }

    const withoutCurrent = word.alternatives.filter(alt => alt.word !== word.word);
    return withoutCurrent.length > 0;
  }, []);

  // Fetch word alternatives from AI service
  const fetchWordAlternatives = useCallback(async (word, segmentIndex, wordIndex) => {
    if (!transcriptId) {
      console.warn('[Transcript] Cannot fetch alternatives: no transcript ID');
      return null;
    }

    // Use ORIGINAL indices for backend API (not processed segment indices)
    const origSegIdx = word.originalSegmentIndex ?? segmentIndex;
    const origWordIdx = word.originalWordIndex ?? wordIndex;

    debugLog('[Transcript] fetchWordAlternatives:', {
      word: word.word,
      processedIndices: { segmentIndex, wordIndex },
      originalIndices: { origSegIdx, origWordIdx }
    });

    try {
      // Use batch endpoint with SSE for better performance and avoid timeouts
      const authToken = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`/api/transcripts/${transcriptId}/batch-word-alternatives`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          words: [{
            word: word.word,
            segmentIndex: origSegIdx,
            wordIndex: origWordIdx,
            confidence: word.confidence ?? word.probability ?? 0
          }]
        })
      });

      if (!response.ok) {
        console.error('Failed to fetch alternatives:', response.status);
        return null;
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let alternatives = null;

      const processStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            try {
              const text = decoder.decode(value);
              const lines = text.split('\n');

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const eventData = JSON.parse(line.substring(6));

                    if (eventData.segmentIndex != null && eventData.wordIndex != null && eventData.alternatives) {
                      // Update the word with alternatives
                      if (!dataRef.current) {
                        console.warn('[Transcript] SSE: dataRef.current is null, skipping alternatives update', {
                          segmentIndex: eventData.segmentIndex,
                          wordIndex: eventData.wordIndex,
                          alternativesCount: eventData.alternatives.length
                        });
                        return;
                      }
                      debugLog('[Transcript] SSE: Before cloning, dataRef.current exists:', !!dataRef.current, 'segments:', dataRef.current?.segments?.length);
                      const newData = JSON.parse(JSON.stringify(dataRef.current));
                      debugLog('[Transcript] SSE: After cloning, newData exists:', !!newData, 'segments:', newData?.segments?.length);
                      if (newData.segments[eventData.segmentIndex]?.words[eventData.wordIndex]) {
                        newData.segments[eventData.segmentIndex].words[eventData.wordIndex].alternatives = eventData.alternatives;
                        debugLog('[Transcript] Alternatives loaded via SSE:', eventData.alternatives.length);
                      }
                      debugLog('[Transcript] SSE: Calling onTranscriptChange with data:', !!newData, 'segments:', newData?.segments?.length);
                      onTranscriptChange(newData);
                      alternatives = eventData.alternatives;
                    }
                  } catch (e) {
                    console.error('[Transcript] Failed to parse SSE:', e);
                  }
                }
              }
            } catch (decodeError) {
              console.error('[Transcript] Error decoding SSE value:', decodeError);
            }
          }
        } catch (error) {
          console.error('[Transcript] Error processing SSE stream:', error);
        }
      };

      await processStream();
      return alternatives;
    } catch (error) {
      console.error('Error fetching word alternatives:', error);
      return null;
    }
  }, [transcriptId, onTranscriptChange]);

  // Auto-fetch alternatives for low-confidence words using batch endpoint
  const [fetchingAlternatives, setFetchingAlternatives] = useState(new Set());
  const fetchedTranscriptsRef = useRef(new Set()); // Track which transcripts have had alternatives fetched
  const [alternativesLoadingCount, setAlternativesLoadingCount] = useState(0);
  const [alternativesTotalCount, setAlternativesTotalCount] = useState(0);

  // Reset loading counts when transcriptId changes (new transcript loaded)
  useEffect(() => {
    setAlternativesLoadingCount(0);
    setAlternativesTotalCount(0);
  }, [transcriptId]);

  // Handle copy events - track copies and ensure proper spacing
  useEffect(() => {
    const transcriptElement = transcriptContentRef.current;
    if (!transcriptElement) return;

    const handleCopy = (e) => {
      try {
        const selection = window.getSelection();
        const selectedText = selection?.toString() || '';

        if (!selectedText || selectedText.length === 0) return;

        // Get the selected HTML to preserve token structure
        const range = selection.getRangeAt(0);
        const fragment = range.cloneContents();

        // Create a temporary container
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(fragment);

        // Extract text from tokens, adding spaces between them
        const tokens = tempDiv.querySelectorAll('.token');
        let spacedText = '';

        if (tokens.length > 0) {
          // If we have token elements, extract text with spaces
          tokens.forEach((token, index) => {
            const tokenText = token.querySelector('.token-text');
            if (tokenText) {
              spacedText += tokenText.textContent;
              // Add space after each token except the last one
              if (index < tokens.length - 1) {
                spacedText += ' ';
              }
            }
          });
        } else {
          // Fallback to selected text if no tokens found
          spacedText = selectedText;
        }

        // Update clipboard with properly spaced text
        if (spacedText) {
          e.preventDefault();
          e.clipboardData.setData('text/plain', spacedText);

          // Track the copy event on the backend
          if (transcriptId) {
            fetch(`/api/transcripts/${transcriptId}/track-copy`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                copiedText: spacedText,
                textLength: spacedText.length,
                timestamp: new Date().toISOString()
              })
            }).catch(err => {
              console.warn('[Transcript] Failed to track copy event:', err);
            });
          }
        }
      } catch (error) {
        console.error('[Transcript] Error handling copy event:', error);
        // Let default copy behavior proceed if our custom handling fails
      }
    };

    transcriptElement.addEventListener('copy', handleCopy);

    return () => {
      transcriptElement.removeEventListener('copy', handleCopy);
    };
  }, [transcriptId]);

  useEffect(() => {
    // DISABLED: Auto-loading alternatives on page load
    // Alternatives should only be loaded manually via context menu
    return;

    // Skip if we've already fetched for this transcript ID
    if (!transcriptId || !data?.segments || fetchedTranscriptsRef.current.has(transcriptId)) return;

    // Find words with low confidence that don't have alternatives yet, sorted by confidence (lowest first)
    const lowConfidenceWords = [];

    data.segments.forEach((segment, segmentIndex) => {
      if (!segment.words) return;

      segment.words.forEach((word, wordIndex) => {
        const confidence = word.confidence ?? word.probability;

        // Auto-fetch alternatives for all words that don't have them yet
        // This allows even high-confidence words to have alternatives
        if (
          !word.isInserted &&
          !word.isGhosted &&
          confidence != null &&
          (!word.alternatives || word.alternatives.length === 0)
        ) {
          lowConfidenceWords.push({ word: word.word, segmentIndex, wordIndex, confidence });
        }
      });
    });

    // Sort by confidence (lowest first) - prioritize worst words
    lowConfidenceWords.sort((a, b) => a.confidence - b.confidence);

    if (lowConfidenceWords.length > 0) {
      debugLog(`[Transcript] Batch fetching alternatives for ${lowConfidenceWords.length} low-confidence words`);
      fetchedTranscriptsRef.current.add(transcriptId); // Mark this transcript as fetched
      setAlternativesTotalCount(lowConfidenceWords.length);
      setAlternativesLoadingCount(lowConfidenceWords.length);

      // Send the batch request
      fetch(`/api/transcripts/${transcriptId}/batch-word-alternatives`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          words: lowConfidenceWords
        })
      }).then(response => {
        if (!response.ok) {
          throw new Error('Batch request failed');
        }

        // Process SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        function processStream() {
          reader.read().then(({ done, value }) => {
            if (done) {
              debugLog('[Transcript] Batch alternatives fetch complete');
              return;
            }

            try {
              const text = decoder.decode(value);
              const lines = text.split('\n');

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                  const eventData = JSON.parse(line.substring(6));

                  if (eventData.type === 'complete') {
                    debugLog('[Transcript] All alternatives fetched');
                    setAlternativesLoadingCount(0);
                    return;
                  }

                  if (eventData.segmentIndex != null && eventData.wordIndex != null) {
                    // Decrement loading count
                    setAlternativesLoadingCount(prev => Math.max(0, prev - 1));

                    if (eventData.alternatives) {
                      // Update the word with alternatives
                      if (!dataRef.current) {
                        console.warn('[Transcript] Batch: dataRef.current is null, skipping alternatives update', {
                          segmentIndex: eventData.segmentIndex,
                          wordIndex: eventData.wordIndex,
                          alternativesCount: eventData.alternatives.length
                        });
                        return;
                      }
                      debugLog('[Transcript] Batch: Before cloning, dataRef.current exists:', !!dataRef.current, 'segments:', dataRef.current?.segments?.length);
                      const newData = JSON.parse(JSON.stringify(dataRef.current));
                      debugLog('[Transcript] Batch: After cloning, newData exists:', !!newData, 'segments:', newData?.segments?.length);
                      if (newData.segments[eventData.segmentIndex]?.words[eventData.wordIndex]) {
                        newData.segments[eventData.segmentIndex].words[eventData.wordIndex].alternatives = eventData.alternatives;
                      }
                      debugLog('[Transcript] Batch: Calling onTranscriptChange with data:', !!newData, 'segments:', newData?.segments?.length);
                      onTranscriptChange(newData);
                    }
                  }
                  } catch (e) {
                    console.error('[Transcript] Error parsing SSE data:', e);
                  }
                }
              }
            } catch (decodeError) {
              console.error('[Transcript] Error decoding SSE stream:', decodeError);
            }

            processStream();
          }).catch(streamError => {
            console.error('[Transcript] Error reading SSE stream:', streamError);
          });
        }

        processStream();
      }).catch(error => {
        console.error('[Transcript] Batch alternatives fetch error:', error);
        setAlternativesLoadingCount(0);
      });
    } else {
      // No low-confidence words to fetch, mark this transcript as processed
      fetchedTranscriptsRef.current.add(transcriptId);
    }
  }, [data, transcriptId, onTranscriptChange]);

  const handleContextShowAlternatives = useCallback(async (selectedWord) => {
    if (selectedWord && selectedWord.element) {
      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      const targetWord = allWords[globalIndex];

      if (targetWord) {
        const tokenId = `${targetWord.segmentIndex}-${targetWord.wordIndex}`;

        // Check if word has alternatives
        if (hasValidAlternatives(targetWord)) {
          // Show existing alternatives
          setOpenAlternativesId(tokenId);
        } else if (!targetWord.isInserted && transcriptId) {
          // Fetch alternatives for words that don't have them
          debugLog('[Transcript] Fetching alternatives for word:', targetWord.word);
          setOpenAlternativesId(tokenId);  // Show loading state

          const alternatives = await fetchWordAlternatives(targetWord, targetWord.segmentIndex, targetWord.wordIndex);

          if (alternatives && alternatives.length > 0) {
            // Alternatives were added to the word, keep dropdown open
            // The dropdown should stay open because we set it before fetching
            debugLog('[Transcript] Alternatives fetched, keeping dropdown open');
          } else {
            // No alternatives available - keep dropdown open to show "No alternatives" message
            debugLog('[Transcript] No alternatives returned for word:', targetWord.word, '- showing message');
            // Keep setOpenAlternativesId(tokenId) as is from above
          }
        } else {
          // Inserted word or no transcriptId - just open it to show "no alternatives" message
          setOpenAlternativesId(tokenId);
        }
      }
    }
  }, [allWords, hasValidAlternatives, transcriptId, fetchWordAlternatives]);

  // Handle alternatives toggle from suggestions mode (clicking a word)
  const handleAlternativesToggle = useCallback(async (tokenId) => {
    // If null, just close
    if (!tokenId) {
      setOpenAlternativesId(null);
      return;
    }

    // Parse tokenId to find the word
    const [segmentIndex, wordIndex] = tokenId.split('-').map(Number);
    const targetWord = allWords.find(w => w.segmentIndex === segmentIndex && w.wordIndex === wordIndex);

    if (!targetWord) {
      console.warn('[Transcript] Could not find word for tokenId:', tokenId);
      return;
    }

    // Check if word has alternatives
    if (hasValidAlternatives(targetWord)) {
      // Show existing alternatives
      setOpenAlternativesId(tokenId);
    } else if (!targetWord.isInserted && transcriptId) {
      // Fetch alternatives for words that don't have them
      debugLog('[Transcript] Fetching alternatives for word in suggestions mode:', targetWord.word);
      setOpenAlternativesId(tokenId);  // Show loading state

      const alternatives = await fetchWordAlternatives(targetWord, targetWord.segmentIndex, targetWord.wordIndex);

      if (alternatives && alternatives.length > 0) {
        // Alternatives were added to the word, keep dropdown open
        debugLog('[Transcript] Alternatives fetched, keeping dropdown open');
      } else {
        // No alternatives available - keep dropdown open to show "No alternatives" message
        debugLog('[Transcript] No alternatives returned for word:', targetWord.word, '- showing message');
        // Keep setOpenAlternativesId(tokenId) as is from above
      }
    } else {
      // Just open it anyway (will show "no alternatives" message)
      setOpenAlternativesId(tokenId);
    }
  }, [allWords, hasValidAlternatives, transcriptId, fetchWordAlternatives]);

  const handleContextInsertWord = useCallback((selectedWord, position) => {
    if (selectedWord && selectedWord.element) {
      // Enable typing mode if not already enabled
      if (!typingMode && onTypingModeToggle) {
        setTypingModeEntryMethod('context-menu');
        onTypingModeToggle();
      }

      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      const targetWord = allWords[globalIndex];

      if (!targetWord) {
        console.warn('[Transcript] Target word not found in allWords for insert at globalIndex:', globalIndex);
        return;
      }

      const segmentIndex = targetWord.segmentIndex;
      const wordIndex = targetWord.wordIndex;

      const newData = JSON.parse(JSON.stringify(data));

      // Find the word in original data.segments by matching text and timestamps
      let foundSegmentIndex = -1;
      let foundWordIndex = -1;

      for (let si = 0; si < newData.segments.length; si++) {
        const segment = newData.segments[si];
        if (!segment.words) continue;

        for (let wi = 0; wi < segment.words.length; wi++) {
          const word = segment.words[wi];
          const textsMatch = word.word === targetWord.word;
          const timestampsMatch = (word.start === targetWord.start && word.end === targetWord.end);
          const isInsertedWord = (targetWord.start === null || targetWord.start === undefined);

          if (textsMatch && (isInsertedWord || timestampsMatch)) {
            if (!isInsertedWord || foundSegmentIndex === -1) {
              foundSegmentIndex = si;
              foundWordIndex = wi;
              break;
            }
          }
        }
        if (foundSegmentIndex !== -1) break;
      }

      if (foundSegmentIndex === -1) {
        console.error('[Transcript] Word not found in original data for insert:', targetWord);
        return;
      }

      // Insert new word at correct position
      const insertionIndex = position === 'before' ? foundWordIndex : foundWordIndex + 1;
      const newWord = {
        word: '',
        start: null,
        end: null,
        confidence: null,
        isInserted: true,
        isGhosted: false
      };

      newData.segments[foundSegmentIndex].words.splice(insertionIndex, 0, newWord);

      // Assign speaker to the new word based on context
      newWord.speaker = assignSpeakerToNewWord(newData, foundSegmentIndex, insertionIndex);
      onTranscriptChange(newData);

      // Calculate global index for the new word and select it
      setTimeout(() => {
        const wordsBeforeInsertion = newData.segments.slice(0, foundSegmentIndex)
          .reduce((acc, seg) => acc + seg.words.length, 0) + insertionIndex;
        setSelectedWordIndex(wordsBeforeInsertion);
        setCursorPosition(0);
      }, 0);
    }
  }, [typingMode, onTypingModeToggle, data, onTranscriptChange, allWords]);

  useEffect(() => {
    // On desktop, only scroll during audio playback (not in typing mode)
    if (!isMobile && typingMode) {
      return;
    }

    // Skip scrolling if this is a typing change
    if (isTypingChange.current) {
      isTypingChange.current = false;
      return;
    }

    // Scroll to selected word on mobile, or on desktop when not in typing mode
    if (selectedWordIndex !== null && wordRefs.current[selectedWordIndex] && wordRefs.current[selectedWordIndex].current) {
      const scrollBehavior = isMobile ? 'auto' : 'smooth';

      if (isMobile) {
        // On mobile, scroll with offset to avoid controls
        const element = wordRefs.current[selectedWordIndex].current;
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        // Get dynamically computed mobile controls height
        const controlsHeight = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--mobile-controls-wrapper-height')
        ) || 221;
        const offset = absoluteElementTop - controlsHeight - 20; // Extra 20px buffer

        window.scrollTo({
          top: offset,
          behavior: scrollBehavior
        });
      } else {
        // On desktop, scroll to keep word visible with offset for header/controls
        const element = wordRefs.current[selectedWordIndex].current;
        const elementRect = element.getBoundingClientRect();

        // Check if element is out of view
        const viewportHeight = window.innerHeight;
        const headerOffset = 200; // Offset for header and controls at top
        const bottomOffset = 100; // Offset at bottom

        const isAboveView = elementRect.top < headerOffset;
        const isBelowView = elementRect.bottom > (viewportHeight - bottomOffset);

        if (isAboveView || isBelowView) {
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const targetPosition = absoluteElementTop - (viewportHeight / 3); // Position at upper third

          window.scrollTo({
            top: targetPosition,
            behavior: scrollBehavior
          });
        }
      }
    }

    // Handle mobile input when word selection changes
    if (typingMode && isMobile) {
      if (selectedWordIndex !== null) {
        // Update mobile input value to match selected word
        const word = allWords[selectedWordIndex];
        if (word) {
          setMobileEditValue(word.word || '');
        }

        // Focus the hidden input without delay to maintain keyboard
        if (mobileInputRef.current && document.activeElement !== mobileInputRef.current) {
          mobileInputRef.current.focus({ preventScroll: true });
          // Set cursor position to end
          mobileInputRef.current.setSelectionRange(
            mobileInputRef.current.value.length,
            mobileInputRef.current.value.length
          );
        }
      } else {
        // Clear mobile input when no word is selected
        setMobileEditValue('');
        if (mobileInputRef.current) {
          mobileInputRef.current.blur();
        }
      }
    }
  }, [selectedWordIndex, typingMode, isMobile, allWords]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to first word on initial load (desktop only)
  useEffect(() => {
    if (!isMobile && allWords.length > 0 && wordRefs.current[0]?.current) {
      // Small delay to ensure layout is complete
      const timer = setTimeout(() => {
        if (wordRefs.current[0]?.current) {
          wordRefs.current[0].current.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [allWords.length, isMobile]);

  // Mobile typing mode management
  useEffect(() => {
    if (typingMode && isMobile) {
      document.body.classList.add('mobile-typing-active');
    } else {
      document.body.classList.remove('mobile-typing-active');
    }

    return () => {
      document.body.classList.remove('mobile-typing-active');
    };
  }, [typingMode, isMobile]);

  const confidenceStats = useMemo(() => {
    const stats = { high: 0, medium: 0, low: 0 };
    allWords.forEach(word => {
      const conf = word.confidence ?? word.probability;
      if (conf === null || conf === undefined) return;
      if (conf >= highThreshold) stats.high++;
      else if (conf >= mediumThreshold) stats.medium++;
      else stats.low++;
    });
    return stats;
  }, [allWords, highThreshold, mediumThreshold]);

  const getConfidenceClass = useCallback((confidence) => {
    if (confidence === null || confidence === undefined) return 'inserted';
    if (confidence >= highThreshold) return 'high-confidence';
    if (confidence >= mediumThreshold) return 'medium-confidence';
    return 'low-confidence';
  }, [highThreshold, mediumThreshold]);

  const findNavigableWord = useCallback((startIndex, direction) => {
    const step = direction === 'next' ? 1 : -1;
    for (let i = startIndex + step; i >= 0 && i < allWords.length; i += step) {
      if (!allWords[i].isGhosted) return i;
    }
    return null;
  }, [allWords]);

  // Helper function to find the correct insertion point after ghosted words
  const findInsertionPointAfterGhosts = useCallback((segmentIndex, wordIndex) => {
    const segment = data.segments[segmentIndex];
    let insertionIndex = wordIndex + 1;
    
    // Skip over any consecutive ghosted words to find the right insertion point
    while (insertionIndex < segment.words.length && segment.words[insertionIndex].isGhosted) {
      insertionIndex++;
    }
    
    return insertionIndex;
  }, [data]);

  // Helper function to join two words and handle the data structure updates
  const joinWords = useCallback((word1Info, word2Info, isBackspace, newData) => {
    // Don't trim whitespace - preserve tabs and newlines
    const joinedWord = (word1Info.word || '') + (word2Info.word || '');

    if (word1Info.segmentIndex === word2Info.segmentIndex) {
      // Same segment
      const segmentIndex = word1Info.segmentIndex;

      // For Backspace: word1 is previous, word2 is current - keep at current position
      // For Delete: word1 is current, word2 is next - keep at current position
      const currentWordIndex = isBackspace ? word2Info.wordIndex : word1Info.wordIndex;
      const otherWordIndex = isBackspace ? word1Info.wordIndex : word2Info.wordIndex;

      // Ghost/remove the other word first if it's at a higher index
      if (otherWordIndex > currentWordIndex) {
        const otherWord = newData.segments[segmentIndex].words[otherWordIndex];
        if (otherWord?.isInserted) {
          newData.segments[segmentIndex].words.splice(otherWordIndex, 1);
        } else if (otherWord) {
          otherWord.isGhosted = true;
        }
      }

      // Ghost/remove current word
      const currentWord = newData.segments[segmentIndex].words[currentWordIndex];
      if (currentWord?.isInserted) {
        newData.segments[segmentIndex].words.splice(currentWordIndex, 1);
      } else if (currentWord) {
        currentWord.isGhosted = true;
      }

      // Ghost/remove the other word if it's at a lower index
      if (otherWordIndex < currentWordIndex) {
        const otherWord = newData.segments[segmentIndex].words[otherWordIndex];
        if (otherWord?.isInserted) {
          newData.segments[segmentIndex].words.splice(otherWordIndex, 1);
        } else if (otherWord) {
          otherWord.isGhosted = true;
        }
      }

      // Insert new joined word at current word's position
      // Account for removed words before current position
      let adjustedIndex = currentWordIndex;
      if (otherWordIndex < currentWordIndex) {
        // Other word was before current, so if it was removed (not ghosted), adjust index
        const otherWord = isBackspace ? word1Info : word2Info;
        if (otherWord.isInserted) {
          adjustedIndex--;
        }
      }

      const newWord = {
        word: joinedWord, start: null, end: null, confidence: null,
        isInserted: true, isGhosted: false,
      };
      newData.segments[segmentIndex].words.splice(adjustedIndex, 0, newWord);

      // Assign speaker to the new word based on context
      newWord.speaker = assignSpeakerToNewWord(newData, segmentIndex, adjustedIndex);

      const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + adjustedIndex;
      const cursorPos = isBackspace ? (word1Info.word || '').trimEnd().length : (word1Info.word || '').trimEnd().length;
      return { newGlobalIndex: wordsBeforeInsertion, cursor: cursorPos };
    } else {
      // Different segments - handle each separately
      const firstSegmentIndex = isBackspace ? word1Info.segmentIndex : word1Info.segmentIndex;
      const firstWordIndex = isBackspace ? word1Info.wordIndex : word1Info.wordIndex;
      const secondSegmentIndex = isBackspace ? word2Info.segmentIndex : word2Info.segmentIndex;
      const secondWordIndex = isBackspace ? word2Info.wordIndex : word2Info.wordIndex;
      
      // Ghost/remove the first word
      const firstWord = newData.segments[firstSegmentIndex]?.words?.[firstWordIndex];
      if (firstWord?.isInserted) {
        newData.segments[firstSegmentIndex].words.splice(firstWordIndex, 1);
      } else if (firstWord) {
        firstWord.isGhosted = true;
      }
      
      // Ghost/remove the second word
      const secondWord = newData.segments[secondSegmentIndex]?.words?.[secondWordIndex];
      if (secondWord?.isInserted) {
        newData.segments[secondSegmentIndex].words.splice(secondWordIndex, 1);
      } else if (secondWord) {
        secondWord.isGhosted = true;
      }
      
      // Insert new joined word at the first word's position
      const newWord = {
        word: joinedWord, start: null, end: null, confidence: null,
        isInserted: true, isGhosted: false,
      };

      const insertionPoint = findInsertionPointAfterGhosts(firstSegmentIndex, firstWordIndex);
      newData.segments[firstSegmentIndex].words.splice(insertionPoint, 0, newWord);

      // Assign speaker to the new word based on context
      newWord.speaker = assignSpeakerToNewWord(newData, firstSegmentIndex, insertionPoint);
      
      const wordsBeforeInsertion = newData.segments.slice(0, firstSegmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
      const cursorPos = isBackspace ? (word1Info.word || '').trimEnd().length : (word1Info.word || '').trimEnd().length;
      return { newGlobalIndex: wordsBeforeInsertion, cursor: cursorPos };
    }
  }, [findInsertionPointAfterGhosts]);

  const clickDeleteWord = useCallback((segmentIndex, wordIndex) => {
    debugLog('[Transcript] clickDeleteWord called with:', { segmentIndex, wordIndex });

    // Find what word we're TRYING to delete from allWords (which uses processedSegments indices)
    const targetWord = allWords.find(w => w.segmentIndex === segmentIndex && w.wordIndex === wordIndex);
    debugLog('[Transcript] Target word from allWords:', targetWord?.word, targetWord);

    if (!targetWord) {
      console.warn('[Transcript] Target word not found in allWords');
      return;
    }

    const newData = JSON.parse(JSON.stringify(data));

    // Since processedSegments regroups words differently than data.segments,
    // we need to find the word in the original data structure by matching
    // the word text and timestamp (start/end)
    let foundSegmentIndex = -1;
    let foundWordIndex = -1;

    // For inserted words, track how many we've seen to match by position
    let insertedWordsSeenInAllWords = 0;
    let insertedWordsSeenInData = 0;

    for (let si = 0; si < newData.segments.length; si++) {
      const segment = newData.segments[si];
      if (!segment.words) continue;

      for (let wi = 0; wi < segment.words.length; wi++) {
        const word = segment.words[wi];
        // Match by word text and timestamps
        const textsMatch = word.word === targetWord.word;
        const timestampsMatch = (word.start === targetWord.start && word.end === targetWord.end);
        const isInsertedWord = (targetWord.start === null || targetWord.start === undefined);

        if (isInsertedWord && word.isInserted) {
          // For inserted words, match by position in the inserted words sequence
          if (textsMatch) {
            // Count how many inserted words with this text we've seen in allWords before target
            const targetGlobalIndex = targetWord.globalIndex;
            insertedWordsSeenInAllWords = allWords.slice(0, targetGlobalIndex).filter(w =>
              w.isInserted && w.word === targetWord.word
            ).length;

            // If this is the Nth occurrence in data, it should match the Nth occurrence in allWords
            if (insertedWordsSeenInData === insertedWordsSeenInAllWords) {
              foundSegmentIndex = si;
              foundWordIndex = wi;
              break;
            }
            insertedWordsSeenInData++;
          }
        } else if (!isInsertedWord && timestampsMatch && textsMatch) {
          // For non-inserted words, match by both text and timestamps
          foundSegmentIndex = si;
          foundWordIndex = wi;
          break;
        }
      }
      if (foundSegmentIndex !== -1) break;
    }

    if (foundSegmentIndex === -1 || foundWordIndex === -1) {
      console.error('[Transcript] Word not found in original data.segments:', {
        targetWord: targetWord.word,
        start: targetWord.start,
        end: targetWord.end,
        totalSegments: newData.segments.length,
        totalWordsInAllSegments: newData.segments.reduce((sum, seg) => sum + (seg.words?.length || 0), 0)
      });

      // Try to find similar words for debugging
      const similarWords = [];
      for (let si = 0; si < newData.segments.length; si++) {
        const segment = newData.segments[si];
        if (!segment.words) continue;
        for (let wi = 0; wi < segment.words.length; wi++) {
          const word = segment.words[wi];
          if (word.word === targetWord.word) {
            similarWords.push({ segmentIndex: si, wordIndex: wi, start: word.start, end: word.end });
          }
        }
      }
      console.error('[Transcript] Similar words found by text match:', similarWords);
      return;
    }

    const word = newData.segments[foundSegmentIndex].words[foundWordIndex];
    debugLog('[Transcript] Found word in data.segments:', word.word, 'at', foundSegmentIndex, foundWordIndex, 'isInserted:', word.isInserted, 'isGhosted:', word.isGhosted);

    // Check if the word being deleted is the currently selected word
    const isCurrentlySelected = selectedWordIndex !== null &&
                                 allWords[selectedWordIndex]?.segmentIndex === segmentIndex &&
                                 allWords[selectedWordIndex]?.wordIndex === wordIndex;

    if (word.isInserted) {
      debugLog('[Transcript] Removing inserted word');
      newData.segments[foundSegmentIndex].words.splice(foundWordIndex, 1);
    } else {
      debugLog('[Transcript] Toggling ghosted state from', word.isGhosted, 'to', !word.isGhosted);
      word.isGhosted = !word.isGhosted;
    }

    // Clear selection if we just deleted/ghosted the selected word
    if (isCurrentlySelected) {
      setSelectedWordIndex(null);
      setCursorPosition(0);
    }

    onTranscriptChange(newData);
  }, [data, onTranscriptChange, allWords, selectedWordIndex]);

  const handleContextDeleteWord = useCallback((selectedWord) => {
    if (selectedWord && selectedWord.element) {
      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      const wordInfo = allWords[globalIndex];

      if (!wordInfo) {
        console.warn('[Transcript] Word not found for globalIndex:', globalIndex);
        return;
      }

      const segmentIndex = wordInfo.segmentIndex;
      const wordIndex = wordInfo.wordIndex;
      debugLog('Delete word at', segmentIndex, wordIndex);
      clickDeleteWord(segmentIndex, wordIndex);
    }
  }, [clickDeleteWord, allWords]);

  const handleContextUndeleteWord = useCallback((selectedWord) => {
    if (selectedWord && selectedWord.element) {
      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      const wordInfo = allWords[globalIndex];

      if (!wordInfo) {
        console.warn('[Transcript] Word not found for globalIndex:', globalIndex);
        return;
      }

      // Find the word in the original data structure
      const newData = JSON.parse(JSON.stringify(data));

      // Navigate through segments to find the word matching by text and timestamps
      for (let si = 0; si < newData.segments.length; si++) {
        const segment = newData.segments[si];
        if (!segment.words) continue;

        for (let wi = 0; wi < segment.words.length; wi++) {
          const word = segment.words[wi];
          if (word.word === wordInfo.word &&
              word.start === wordInfo.start &&
              word.end === wordInfo.end &&
              word.isGhosted) {
            // Un-ghost the word
            word.isGhosted = false;
            debugLog('[Transcript] Undeleted word:', word.word);
            onTranscriptChange(newData);
            return;
          }
        }
      }

      console.warn('[Transcript] Could not find ghosted word to undelete');
    }
  }, [allWords, data, onTranscriptChange]);

  const handleChangeSpeaker = useCallback(async (selectedWord, newSpeaker) => {
    if (!selectedWord || !selectedWord.element || !transcriptId) return;

    const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
    const wordInfo = allWords[globalIndex];

    if (!wordInfo) {
      console.warn('[Transcript] Word not found for globalIndex:', globalIndex);
      return;
    }

    try {
      // Update word speaker via API
      const response = await fetch(`/api/transcripts/${transcriptId}/words/${wordInfo.segmentIndex}/${wordInfo.wordIndex}/speaker`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ speaker: newSpeaker })
      });

      if (response.ok) {
        // Update local data
        const newData = JSON.parse(JSON.stringify(data));
        if (newData.segments[wordInfo.segmentIndex]?.words[wordInfo.wordIndex]) {
          newData.segments[wordInfo.segmentIndex].words[wordInfo.wordIndex].speaker = newSpeaker;
          debugLog('[Transcript] Updated word speaker:', wordInfo.word, '->', newSpeaker);
          onTranscriptChange(newData);
        }
      } else {
        console.error('[Transcript] Failed to update word speaker:', response.status);
      }
    } catch (err) {
      console.error('[Transcript] Error updating word speaker:', err);
    }
  }, [allWords, data, transcriptId, onTranscriptChange]);

  const handleSplitSpeaker = useCallback(async (selectedWordRange, newSpeaker) => {
    if (!selectedWordRange || !newSpeaker || !transcriptId) return;

    const { startWordIndex, endWordIndex } = selectedWordRange;
    debugLog('[Transcript] Splitting speaker for words', startWordIndex, 'to', endWordIndex, '-> speaker:', newSpeaker);

    // Get current speaker of the first word in selection
    const firstWord = allWords[startWordIndex];
    const fromLabel = firstWord?.speaker;

    if (!fromLabel) {
      console.warn('[Transcript] No speaker found for selected words');
      return;
    }

    // Build wordRanges array for the API
    // Group consecutive words by segment
    const wordRanges = [];
    let currentRange = null;

    for (let globalIdx = startWordIndex; globalIdx <= endWordIndex; globalIdx++) {
      const wordInfo = allWords[globalIdx];
      if (!wordInfo) continue;

      if (!currentRange || currentRange.segmentIndex !== wordInfo.segmentIndex) {
        // Start new range
        if (currentRange) {
          wordRanges.push(currentRange);
        }
        currentRange = {
          segmentIndex: wordInfo.segmentIndex,
          startWord: wordInfo.wordIndex,
          endWord: wordInfo.wordIndex
        };
      } else {
        // Extend current range
        currentRange.endWord = wordInfo.wordIndex;
      }
    }

    if (currentRange) {
      wordRanges.push(currentRange);
    }

    debugLog('[Transcript] Computed wordRanges:', wordRanges);

    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/speakers/split`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          fromLabel,
          newLabel: newSpeaker,
          wordRanges
        })
      });

      if (response.ok) {
        const result = await response.json();
        debugLog('[Transcript] Speaker split successful:', result);

        // Update local data to reflect the changes
        const newData = JSON.parse(JSON.stringify(data));
        for (const range of wordRanges) {
          const segment = newData.segments[range.segmentIndex];
          if (segment && segment.words) {
            for (let i = range.startWord; i <= range.endWord; i++) {
              if (segment.words[i]) {
                segment.words[i].speaker = newSpeaker;
              }
            }
          }
        }
        onTranscriptChange(newData);

        // Refresh speaker data
        handleSpeakerUpdated();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[Transcript] Failed to split speaker:', errorData);
      }
    } catch (err) {
      console.error('[Transcript] Error splitting speaker:', err);
    }
  }, [allWords, data, transcriptId, onTranscriptChange, handleSpeakerUpdated]);

  const handlePlayWord = useCallback((selectedWord) => {
    if (selectedWord && selectedWord.element && onTokenClick) {
      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      const wordInfo = allWords[globalIndex];

      if (!wordInfo) {
        console.warn('[Transcript] Word not found for globalIndex:', globalIndex);
        return;
      }

      // Only play words that have timing information
      if (wordInfo.start !== null && wordInfo.start !== undefined && wordInfo.end !== null && wordInfo.end !== undefined) {
        // Clean up any existing auto-pause listeners first
        if (autoPauseCleanupRef.current) {
          autoPauseCleanupRef.current();
          autoPauseCleanupRef.current = null;
        }

        // Update selected word for immediate visual feedback
        setSelectedWordIndex(globalIndex);
        debugLog('[Transcript] Playing word:', wordInfo.word, 'from', wordInfo.start, 'to', wordInfo.end);

        // Set up auto-pause AFTER seek completes
        if (audioRef?.current) {
          const audio = audioRef.current;

          // Wait for seek to complete before setting up auto-pause
          const handleSeeked = () => {
            debugLog('[Transcript] Seek completed, setting up auto-pause');

            const handleTimeUpdate = () => {
              if (audio.currentTime >= wordInfo.end) {
                audio.pause();
                audio.removeEventListener('timeupdate', handleTimeUpdate);
                debugLog('[Transcript] Auto-paused at word end:', wordInfo.end);
                autoPauseCleanupRef.current = null;
              }
            };

            audio.addEventListener('timeupdate', handleTimeUpdate);

            // Cleanup listener if audio is paused/ended/seeked before word ends
            const handlePause = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              autoPauseCleanupRef.current = null;
            };
            const handleEnded = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              autoPauseCleanupRef.current = null;
            };
            const handleSeeking = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              autoPauseCleanupRef.current = null;
            };

            audio.addEventListener('pause', handlePause, { once: true });
            audio.addEventListener('ended', handleEnded, { once: true });
            audio.addEventListener('seeking', handleSeeking, { once: true });

            // Store cleanup function
            autoPauseCleanupRef.current = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              audio.removeEventListener('pause', handlePause);
              audio.removeEventListener('ended', handleEnded);
              audio.removeEventListener('seeking', handleSeeking);
            };

            // Ensure audio starts playing
            audio.play().catch(err => console.error('[Transcript] Failed to play audio:', err));
          };

          // Listen for seek completion
          audio.addEventListener('seeked', handleSeeked, { once: true });
        }

        // Seek to the start of the word (will trigger seeked event)
        onTokenClick(wordInfo.start);
      }
    }
  }, [allWords, onTokenClick, audioRef]);

  const handlePlaySentence = useCallback((selectedWord) => {
    if (selectedWord && selectedWord.element && onTokenClick) {
      const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
      const wordInfo = allWords[globalIndex];

      if (!wordInfo) {
        console.warn('[Transcript] Word not found for globalIndex:', globalIndex);
        return;
      }

      // Find sentence boundaries by looking for punctuation marks
      // A sentence ends with: . ! ? or at the end of transcript
      const sentencePunctuation = ['.', '!', '?'];

      // Find start of sentence (look backwards for punctuation)
      let sentenceStartIndex = globalIndex;
      for (let i = globalIndex - 1; i >= 0; i--) {
        const prevWord = allWords[i];
        if (!prevWord || prevWord.isInserted) continue;

        // Check if previous word ends with sentence-ending punctuation
        const lastChar = prevWord.word.trim().slice(-1);
        if (sentencePunctuation.includes(lastChar)) {
          sentenceStartIndex = i + 1; // Start from word after punctuation
          break;
        }
      }

      // Find end of sentence (look forwards for punctuation)
      let sentenceEndIndex = globalIndex;
      for (let i = globalIndex; i < allWords.length; i++) {
        const currentWord = allWords[i];
        if (!currentWord || currentWord.isInserted) continue;

        sentenceEndIndex = i; // Update end index

        // Check if current word ends with sentence-ending punctuation
        const lastChar = currentWord.word.trim().slice(-1);
        if (sentencePunctuation.includes(lastChar)) {
          break;
        }
      }

      const sentenceStart = allWords[sentenceStartIndex];
      const sentenceEnd = allWords[sentenceEndIndex];

      if (sentenceStart && sentenceStart.start !== null && sentenceStart.start !== undefined &&
          sentenceEnd && sentenceEnd.end !== null && sentenceEnd.end !== undefined) {
        // Clean up any existing auto-pause listeners first
        if (autoPauseCleanupRef.current) {
          autoPauseCleanupRef.current();
          autoPauseCleanupRef.current = null;
        }

        // Update selected word for immediate visual feedback (highlight clicked word)
        setSelectedWordIndex(globalIndex);
        debugLog('[Transcript] Playing sentence from', sentenceStart.word, 'to', sentenceEnd.word,
                    'time range:', sentenceStart.start, '-', sentenceEnd.end);

        // Set up auto-pause AFTER seek completes
        if (audioRef?.current) {
          const audio = audioRef.current;

          // Wait for seek to complete before setting up auto-pause
          const handleSeeked = () => {
            debugLog('[Transcript] Seek completed, setting up auto-pause for sentence');

            const handleTimeUpdate = () => {
              if (audio.currentTime >= sentenceEnd.end) {
                audio.pause();
                audio.removeEventListener('timeupdate', handleTimeUpdate);
                debugLog('[Transcript] Auto-paused at sentence end:', sentenceEnd.end);
                autoPauseCleanupRef.current = null;
              }
            };

            audio.addEventListener('timeupdate', handleTimeUpdate);

            // Cleanup listener if audio is paused/ended/seeked before sentence ends
            const handlePause = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              autoPauseCleanupRef.current = null;
            };
            const handleEnded = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              autoPauseCleanupRef.current = null;
            };
            const handleSeeking = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              autoPauseCleanupRef.current = null;
            };

            audio.addEventListener('pause', handlePause, { once: true });
            audio.addEventListener('ended', handleEnded, { once: true });
            audio.addEventListener('seeking', handleSeeking, { once: true });

            // Store cleanup function
            autoPauseCleanupRef.current = () => {
              audio.removeEventListener('timeupdate', handleTimeUpdate);
              audio.removeEventListener('pause', handlePause);
              audio.removeEventListener('ended', handleEnded);
              audio.removeEventListener('seeking', handleSeeking);
            };

            // Ensure audio starts playing
            audio.play().catch(err => console.error('[Transcript] Failed to play audio:', err));
          };

          // Listen for seek completion
          audio.addEventListener('seeked', handleSeeked, { once: true });
        }

        // Seek to the start of the sentence (will trigger seeked event)
        onTokenClick(sentenceStart.start);
      }
    }
  }, [allWords, onTokenClick, audioRef]);

  const handlePause = useCallback(() => {
    if (audioRef?.current) {
      audioRef.current.pause();
      debugLog('[Transcript] Audio paused via context menu');
    }
  }, [audioRef]);

  // Timestamp editing handlers
  const handleEditTimestamp = useCallback((selectedWord) => {
    if (!selectedWord || !selectedWord.element) return;

    const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
    const wordInfo = allWords[globalIndex];
    if (!wordInfo) return;

    const word = data.segments[wordInfo.segmentIndex]?.words[wordInfo.wordIndex];
    if (!word) return;

    // Get previous and next words for context
    const prevWordInfo = globalIndex > 0 ? allWords[globalIndex - 1] : null;
    const nextWordInfo = globalIndex < allWords.length - 1 ? allWords[globalIndex + 1] : null;
    const prevWord = prevWordInfo ? data.segments[prevWordInfo.segmentIndex]?.words[prevWordInfo.wordIndex] : null;
    const nextWord = nextWordInfo ? data.segments[nextWordInfo.segmentIndex]?.words[nextWordInfo.wordIndex] : null;

    setTimestampEditor({
      word,
      wordIndex: wordInfo.wordIndex,
      segmentIndex: wordInfo.segmentIndex,
      globalIndex,
      anchorElement: selectedWord.element,
      previousWord: prevWord,
      nextWord: nextWord
    });
  }, [allWords, data]);

  const handleTimestampEditorSave = useCallback(({ segmentIndex, wordIndex, globalIndex, newStart, newEnd, rippleMode }) => {
    const newData = JSON.parse(JSON.stringify(data));

    // Get all words as a flat array for ripple operations
    const flatWords = [];
    newData.segments.forEach((segment, sIdx) => {
      segment.words?.forEach((word, wIdx) => {
        flatWords.push({ word, segmentIndex: sIdx, wordIndex: wIdx });
      });
    });

    // Apply the timestamp change with ripple effect
    const result = applyTimestampWithRipple(
      flatWords.map(fw => fw.word),
      globalIndex,
      newStart,
      newEnd,
      rippleMode
    );

    if (result.success) {
      // Update segment boundaries for affected segments
      const affectedSegments = new Set(result.affectedIndices.map(idx => flatWords[idx]?.segmentIndex));
      affectedSegments.forEach(sIdx => {
        if (sIdx !== undefined) {
          updateSegmentBoundaries(newData.segments[sIdx]);
        }
      });

      // Sync flat words array
      syncFlatWordsArray(newData);

      // Mark word as timestamp-edited
      const targetWord = newData.segments[segmentIndex]?.words[wordIndex];
      if (targetWord) {
        targetWord.timestampEdited = true;
      }

      onTranscriptChange(newData);
      debugLog('[Transcript] Timestamp updated for word at', segmentIndex, wordIndex, 'new times:', newStart, '-', newEnd);
    }

    setTimestampEditor(null);
  }, [data, onTranscriptChange]);

  const handleTimestampEditorCancel = useCallback(() => {
    setTimestampEditor(null);
  }, []);

  const handleShiftTimestamps = useCallback(() => {
    if (!multiWordSelection) return;

    const { startWordIndex, endWordIndex } = multiWordSelection;
    const selectedWords = [];

    for (let i = startWordIndex; i <= endWordIndex; i++) {
      const wordInfo = allWords[i];
      if (wordInfo) {
        const word = data.segments[wordInfo.segmentIndex]?.words[wordInfo.wordIndex];
        if (word && !word.isGhosted) {
          selectedWords.push({ ...word, globalIndex: i, segmentIndex: wordInfo.segmentIndex, wordIndex: wordInfo.wordIndex });
        }
      }
    }

    if (selectedWords.length > 0) {
      setTimestampShiftDialog({ selectedWords, startWordIndex, endWordIndex });
    }
  }, [multiWordSelection, allWords, data]);

  const handleShiftDialogApply = useCallback((offsetSeconds) => {
    if (!timestampShiftDialog) return;

    const { startWordIndex, endWordIndex } = timestampShiftDialog;
    const newData = JSON.parse(JSON.stringify(data));

    // Get all words as a flat array
    const flatWords = [];
    newData.segments.forEach((segment) => {
      segment.words?.forEach((word) => {
        flatWords.push(word);
      });
    });

    // Apply shift
    const result = shiftTimestamps(flatWords, startWordIndex, endWordIndex, offsetSeconds);

    if (result.success) {
      // Update segment boundaries
      const affectedSegments = new Set();
      for (let i = startWordIndex; i <= endWordIndex; i++) {
        const wordInfo = allWords[i];
        if (wordInfo) {
          affectedSegments.add(wordInfo.segmentIndex);
        }
      }
      affectedSegments.forEach(sIdx => {
        updateSegmentBoundaries(newData.segments[sIdx]);
      });

      // Sync flat words array
      syncFlatWordsArray(newData);

      onTranscriptChange(newData);
      debugLog('[Transcript] Shifted timestamps by', offsetSeconds, 'seconds for', result.affectedIndices.length, 'words');
    }

    setTimestampShiftDialog(null);
  }, [timestampShiftDialog, data, allWords, onTranscriptChange]);

  const handleShiftDialogCancel = useCallback(() => {
    setTimestampShiftDialog(null);
  }, []);

  const handleDistributeTimestamps = useCallback(() => {
    if (!multiWordSelection) return;

    const { startWordIndex, endWordIndex } = multiWordSelection;
    const selectedWords = [];

    for (let i = startWordIndex; i <= endWordIndex; i++) {
      const wordInfo = allWords[i];
      if (wordInfo) {
        const word = data.segments[wordInfo.segmentIndex]?.words[wordInfo.wordIndex];
        if (word && !word.isGhosted) {
          selectedWords.push({ ...word, globalIndex: i, segmentIndex: wordInfo.segmentIndex, wordIndex: wordInfo.wordIndex });
        }
      }
    }

    if (selectedWords.length >= 2) {
      setTimestampDistributeDialog({ selectedWords, startWordIndex, endWordIndex });
    }
  }, [multiWordSelection, allWords, data]);

  const handleDistributeDialogApply = useCallback((mode) => {
    if (!timestampDistributeDialog) return;

    const { startWordIndex, endWordIndex } = timestampDistributeDialog;
    const newData = JSON.parse(JSON.stringify(data));

    // Get all words as a flat array
    const flatWords = [];
    newData.segments.forEach((segment) => {
      segment.words?.forEach((word) => {
        flatWords.push(word);
      });
    });

    // Apply distribution
    const result = distributeTimestamps(flatWords, startWordIndex, endWordIndex, mode);

    if (result.success) {
      // Update segment boundaries
      const affectedSegments = new Set();
      for (let i = startWordIndex; i <= endWordIndex; i++) {
        const wordInfo = allWords[i];
        if (wordInfo) {
          affectedSegments.add(wordInfo.segmentIndex);
        }
      }
      affectedSegments.forEach(sIdx => {
        updateSegmentBoundaries(newData.segments[sIdx]);
      });

      // Sync flat words array
      syncFlatWordsArray(newData);

      onTranscriptChange(newData);
      debugLog('[Transcript] Distributed timestamps for', result.affectedIndices.length, 'words using', mode, 'mode');
    }

    setTimestampDistributeDialog(null);
  }, [timestampDistributeDialog, data, allWords, onTranscriptChange]);

  const handleDistributeDialogCancel = useCallback(() => {
    setTimestampDistributeDialog(null);
  }, []);

  const handleBoundaryChange = useCallback((prevGlobalIndex, nextGlobalIndex, newBoundary) => {
    const newData = JSON.parse(JSON.stringify(data));

    // Get all words as a flat array
    const flatWords = [];
    newData.segments.forEach((segment) => {
      segment.words?.forEach((word) => {
        flatWords.push(word);
      });
    });

    // Apply boundary adjustment
    const result = adjustBoundary(flatWords, prevGlobalIndex, newBoundary);

    if (result.success) {
      // Update segment boundaries for affected segments
      const prevWordInfo = allWords[prevGlobalIndex];
      const nextWordInfo = allWords[nextGlobalIndex];
      const affectedSegments = new Set([prevWordInfo?.segmentIndex, nextWordInfo?.segmentIndex].filter(s => s !== undefined));

      affectedSegments.forEach(sIdx => {
        updateSegmentBoundaries(newData.segments[sIdx]);
      });

      // Sync flat words array
      syncFlatWordsArray(newData);

      onTranscriptChange(newData);
      debugLog('[Transcript] Boundary adjusted between words', prevGlobalIndex, 'and', nextGlobalIndex, 'to', newBoundary);
    }
  }, [data, allWords, onTranscriptChange]);

  const handleWordReplacement = useCallback((segmentIndex, wordIndex, newWord) => {
    const newData = JSON.parse(JSON.stringify(data));

    // Safety check for segment and word existence
    if (!newData.segments[segmentIndex] || !newData.segments[segmentIndex].words[wordIndex]) {
      console.warn('Word not found for replacement:', { segmentIndex, wordIndex });
      return;
    }

    const word = newData.segments[segmentIndex].words[wordIndex];

    if (word.isInserted) {
      // For inserted words, just replace the text
      word.word = newWord;
    } else {
      // For original words, ghost the original and insert the replacement
      word.isGhosted = true;
      const replacementWord = {
        word: newWord,
        start: word.start,
        end: word.end,
        confidence: null,
        isInserted: true,
        isGhosted: false
      };

      // Find insertion point after ghosted words
      const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
      newData.segments[segmentIndex].words.splice(insertionPoint, 0, replacementWord);
    }

    onTranscriptChange(newData);
  }, [data, onTranscriptChange, findInsertionPointAfterGhosts]);

  // Handle replacing a word with a dictionary word
  const handleReplaceWithDictionaryWord = useCallback((selectedWord, dictionaryWord) => {
    if (!selectedWord || !selectedWord.element) return;

    const globalIndex = parseInt(selectedWord.element.dataset.globalIndex);
    const wordInfo = allWords[globalIndex];

    if (!wordInfo) {
      console.warn('[Transcript] Word not found for globalIndex:', globalIndex);
      return;
    }

    // Use the existing word replacement handler
    handleWordReplacement(wordInfo.segmentIndex, wordInfo.wordIndex, dictionaryWord);
    debugLog('[Transcript] Replaced word with dictionary word:', dictionaryWord);
  }, [allWords, handleWordReplacement]);

  // Mobile input handler for text editing
  const handleMobileInput = useCallback((e) => {
    if (selectedWordIndex === null || !typingMode || !isMobile) return;

    const newValue = e.target.value;
    const oldValue = mobileEditValue;

    // Detect what changed
    if (newValue.length > oldValue.length) {
      // Character(s) added
      const addedChars = newValue.slice(oldValue.length);
      // Simulate key presses for each added character
      for (const char of addedChars) {
        const syntheticEvent = new KeyboardEvent('keydown', {
          key: char,
          code: `Key${char.toUpperCase()}`,
          bubbles: true
        });
        // Let the document handle it through the existing keyboard handler
        document.dispatchEvent(syntheticEvent);
      }
    } else if (newValue.length < oldValue.length) {
      // Character(s) deleted - simulate backspace
      const deleteCount = oldValue.length - newValue.length;
      for (let i = 0; i < deleteCount; i++) {
        const syntheticEvent = new KeyboardEvent('keydown', {
          key: 'Backspace',
          code: 'Backspace',
          bubbles: true
        });
        document.dispatchEvent(syntheticEvent);
      }
    }

    // Update the mobile edit value to match current word after a short delay
    setTimeout(() => {
      const wordInfo = allWords[selectedWordIndex];
      if (wordInfo) {
        setMobileEditValue(wordInfo.word || '');
      }
    }, 10);
  }, [selectedWordIndex, typingMode, isMobile, mobileEditValue, allWords]);

  // Mobile keyboard handler to intercept special keys
  const handleMobileKeyDown = useCallback((e) => {
    if (!isMobile || !typingMode || selectedWordIndex === null) return;

    // For special keys, prevent default and dispatch to document
    const specialKeys = ['Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Delete', ' '];

    if (specialKeys.includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();

      // Create and dispatch synthetic event
      const syntheticEvent = new KeyboardEvent('keydown', {
        key: e.key,
        code: e.code || `Key${e.key}`,
        bubbles: true,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        metaKey: e.metaKey
      });
      document.dispatchEvent(syntheticEvent);

      // Update mobile input value after handling
      setTimeout(() => {
        const wordInfo = allWords[selectedWordIndex];
        if (wordInfo) {
          setMobileEditValue(wordInfo.word || '');
        }
      }, 10);
    }
  }, [isMobile, typingMode, selectedWordIndex, allWords]);

  const handleWordSelect = useCallback((segmentIndex, wordIndex, action = 'select', cursorPos = null) => {
    const globalIndex = allWords.findIndex(w => w.segmentIndex === segmentIndex && w.wordIndex === wordIndex);
    if (globalIndex === -1) return;

    const word = allWords[globalIndex];

    // Click-to-delete mode works independently of typing mode
    debugLog('[Transcript] handleWordClick - typingMode:', typingMode, 'clickToDeleteMode:', clickToDeleteMode);
    if (clickToDeleteMode) {
      debugLog('[Transcript] Click-to-delete activated, deleting word:', word);
      clickDeleteWord(segmentIndex, wordIndex);
      return;
    }

    if (typingMode) {
      if (word.isGhosted) {
        // Find nearest non-ghosted word
        let nearestIndex = null;
        let minDistance = Infinity;

        allWords.forEach((w, idx) => {
          if (!w.isGhosted) {
            const distance = Math.abs(idx - globalIndex);
            if (distance < minDistance) {
              minDistance = distance;
              nearestIndex = idx;
            }
          }
        });

        if (nearestIndex !== null) {
          const nearestWord = allWords[nearestIndex];
          setSelectedWordIndex(nearestIndex);
          setMobileEditValue(nearestWord.word || '');
          setCursorPosition((nearestWord.word || '').length);
        }
        return;
      }

      // If clicking a different word, exit typing mode
      if (selectedWordIndex !== null && selectedWordIndex !== globalIndex) {
        debugLog('[Transcript] Clicking different word, exiting typing mode');
        if (onTypingModeToggle) {
          onTypingModeToggle();
        }
        // Select the new word in listening mode
        setSelectedWordIndex(globalIndex);
        setCursorPosition(0);
        setMobileEditValue('');
        return;
      }

      // Clicking the same word or no previous selection - stay in typing mode
      setSelectedWordIndex(globalIndex);
      // Set mobile edit value for mobile input
      setMobileEditValue(word.word || '');
      // Use provided cursor position if available, otherwise default to end of word
      if (cursorPos !== null) {
        setCursorPosition(Math.max(0, Math.min(cursorPos, (word.word || '').length)));
      } else {
        setCursorPosition((word.word || '').length);
      }
    } else {
      // In listening mode, selecting a word makes it the anchor for bin navigation
      setSelectedWordIndex(globalIndex);
    }

    // Ensure the transcript content is focusable and focused for keyboard events
    if (transcriptContentRef.current && !isMobile) {
      transcriptContentRef.current.focus({ preventScroll: true });
    }
  }, [typingMode, typingModeEntryMethod, clickToDeleteMode, allWords, clickDeleteWord, selectedWordIndex, isMobile]);

  // Handle double-click to enter typing mode
  const handleDoubleClickEnterTyping = useCallback((segmentIndex, wordIndex, cursorPos) => {
    debugLog('[Transcript] handleDoubleClickEnterTyping', {segmentIndex, wordIndex, cursorPos, typingMode});

    // Enable typing mode if not already enabled
    if (!typingMode && onTypingModeToggle) {
      setTypingModeEntryMethod('double-click');
      onTypingModeToggle();
    }

    // Select the word with calculated cursor position
    const globalIndex = allWords.findIndex(w => w.segmentIndex === segmentIndex && w.wordIndex === wordIndex);
    if (globalIndex === -1) return;

    const word = allWords[globalIndex];

    // Don't select ghosted words
    if (word.isGhosted) return;

    setSelectedWordIndex(globalIndex);
    setMobileEditValue(word.word || '');
    setCursorPosition(Math.max(0, Math.min(cursorPos, (word.word || '').length)));

    // Focus transcript for keyboard events
    if (transcriptContentRef.current && !isMobile) {
      transcriptContentRef.current.focus({ preventScroll: true });
    }
  }, [typingMode, onTypingModeToggle, allWords, isMobile]);

  const navigateInBin = useCallback((direction, startIndex) => {
    const currentWord = allWords[startIndex];
    if (!currentWord) return;
    
    // Add confidenceClass to allWords if it's not there
    const wordsWithConfidence = allWords.map(w => ({...w, confidenceClass: getConfidenceClass(w.confidence ?? w.probability)}));
    const targetClass = wordsWithConfidence[startIndex].confidenceClass;

    const wordsInBin = wordsWithConfidence.filter(w => w.confidenceClass === targetClass && !w.isGhosted);
    const currentIndexInBin = wordsInBin.findIndex(w => w.globalIndex === startIndex);
    
    if (currentIndexInBin === -1) return;
    
    const nextIndexInBin = (currentIndexInBin + (direction === 'next' ? 1 : -1) + wordsInBin.length) % wordsInBin.length;
    const nextWord = wordsInBin[nextIndexInBin];

    if (nextWord) {
      setSelectedWordIndex(nextWord.globalIndex);
      // In listening mode, also seek audio to the new word
      if (!typingMode && onTokenClick) {
        onTokenClick(nextWord.start + 0.01);
      }
    }
  }, [allWords, typingMode, onTokenClick, getConfidenceClass]);

  const navigateToConfidenceBin = useCallback((confidenceClass) => {
    const wordsWithConfidence = allWords.map(w => ({...w, confidenceClass: getConfidenceClass(w.confidence ?? w.probability)}));
    const wordsInBin = wordsWithConfidence.filter(w => w.confidenceClass === confidenceClass && !w.isGhosted);
    
    if (wordsInBin.length === 0) return;
    
    // Find the next word in the bin after the current selection, or start from the beginning
    let startSearchIndex = 0;
    if (selectedWordIndex !== null) {
      const currentWord = allWords[selectedWordIndex];
      if (currentWord && getConfidenceClass(currentWord.confidence ?? currentWord.probability) === confidenceClass) {
        // If we're already in this bin, find the next word
        const currentIndexInBin = wordsInBin.findIndex(w => w.globalIndex === selectedWordIndex);
        if (currentIndexInBin !== -1) {
          startSearchIndex = (currentIndexInBin + 1) % wordsInBin.length;
        }
      }
    }
    
    const targetWord = wordsInBin[startSearchIndex];
    if (targetWord) {
      setSelectedWordIndex(targetWord.globalIndex);
      // In listening mode, also seek audio to the new word
      if (!typingMode && onTokenClick) {
        onTokenClick(targetWord.start + 0.01);
      }
    }
  }, [allWords, selectedWordIndex, typingMode, onTokenClick, getConfidenceClass]);

  useEffect(() => {
    if (!typingMode && selectedWordIndex !== null) {
      const word = allWords[selectedWordIndex];
      if (word && word.start !== null) {
        onTokenClick(word.start + 0.01);
      }
    }
  }, [typingMode, selectedWordIndex, allWords, onTokenClick]);

  const activeWordGlobalIndex = useMemo(() => {
    // If currentTime is null or 0, default to highlighting the first non-ghosted word
    if (currentTime === null || currentTime === 0) {
      const firstNonGhostedIndex = allWords.findIndex(w => !w.isGhosted);
      debugLog('[Transcript] currentTime is null/0, defaulting to first word:', firstNonGhostedIndex);
      return firstNonGhostedIndex >= 0 ? firstNonGhostedIndex : null;
    }

    // Find word whose time range includes currentTime
    let activeIndex = allWords.findIndex(w => !w.isGhosted && currentTime >= w.start && currentTime <= w.end);

    // If no word found and currentTime is beyond the last word, highlight the last word
    if (activeIndex < 0) {
      const lastNonGhostedIndex = allWords.map((w, i) => !w.isGhosted ? i : -1).filter(i => i >= 0).pop();
      if (lastNonGhostedIndex !== undefined && allWords[lastNonGhostedIndex] && currentTime > allWords[lastNonGhostedIndex].end) {
        activeIndex = lastNonGhostedIndex;
      }
    }

    // If still no word found (in gaps between words), find the nearest word before currentTime
    if (activeIndex < 0) {
      const wordsBeforeCurrent = allWords
        .map((w, i) => ({ ...w, index: i }))
        .filter(w => !w.isGhosted && w.end <= currentTime);
      if (wordsBeforeCurrent.length > 0) {
        activeIndex = wordsBeforeCurrent[wordsBeforeCurrent.length - 1].index;
      }
    }

    debugLog('[Transcript] Highlighting calculation:', {
      currentTime,
      totalWords: allWords.length,
      activeIndex,
      activeWord: activeIndex >= 0 ? allWords[activeIndex] : null,
      sampleWords: allWords.slice(0, 3).map(w => ({
        word: w.word,
        start: w.start,
        end: w.end,
        isGhosted: w.isGhosted
      }))
    });

    return activeIndex >= 0 ? activeIndex : null;
  }, [allWords, currentTime]);

  useEffect(() => {
    if (typingMode && selectedWordIndex === null && activeWordGlobalIndex !== null) {
      setSelectedWordIndex(activeWordGlobalIndex);
    }
  }, [typingMode, selectedWordIndex, activeWordGlobalIndex]);

  useEffect(() => {
    if (!typingMode && activeWordGlobalIndex !== null && wordRefs.current[activeWordGlobalIndex] && wordRefs.current[activeWordGlobalIndex].current) {
      const scrollBehavior = isMobile ? 'auto' : 'smooth';
      const element = wordRefs.current[activeWordGlobalIndex].current;
      const container = transcriptContentRef.current;

      if (!container) return;

      if (isMobile) {
        // On mobile, center the active word in the viewport (still use window scroll)
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const controlsHeight = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue('--mobile-controls-wrapper-height')
        ) || 221;

        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - controlsHeight;
        const centerOffset = controlsHeight + (availableHeight / 2) - (elementRect.height / 2);
        const offset = absoluteElementTop - centerOffset;

        window.scrollTo({
          top: offset,
          behavior: scrollBehavior
        });
      } else {
        // On desktop, scroll the transcript-content container only (not the whole page)
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate element position relative to container
        const elementTop = elementRect.top - containerRect.top + container.scrollTop;
        const elementHeight = elementRect.height;
        const containerHeight = container.clientHeight;

        // Center the element in the container
        const scrollTo = elementTop - (containerHeight / 2) + (elementHeight / 2);

        container.scrollTo({
          top: scrollTo,
          behavior: scrollBehavior
        });
      }
    }
  }, [activeWordGlobalIndex, typingMode, isMobile]);

  const [nextSelectionInfo, setNextSelectionInfo] = useState(null);
  const previousSelectedWordRef = useRef(null);

  // Clean up empty inserted words when deselecting or switching words
  useEffect(() => {
    // Check if we're changing selection (includes deselecting to null or switching to another word)
    if (previousSelectedWordRef.current !== null && previousSelectedWordRef.current !== selectedWordIndex) {
      const prevWordInfo = allWords[previousSelectedWordRef.current];

      // Check if the previously selected word was an empty inserted word (unless whitespace mode is on)
      const isWhitespaceOnly = prevWordInfo && prevWordInfo.word && prevWordInfo.word.trim() === '' && prevWordInfo.word.length > 0;
      if (prevWordInfo && prevWordInfo.isInserted && (!prevWordInfo.word || (prevWordInfo.word.trim() === '' && !internalWhitespaceMode))) {
        // Find the word in original data.segments and remove it
        const newData = JSON.parse(JSON.stringify(data));
        let removed = false;

        for (let si = 0; si < newData.segments.length && !removed; si++) {
          const segment = newData.segments[si];
          if (!segment.words) continue;

          for (let wi = 0; wi < segment.words.length; wi++) {
            const word = segment.words[wi];

            // Match by timestamps (both should be null for inserted words) and empty text (unless whitespace mode is on)
            const isWordWhitespaceOnly = word.word && word.word.trim() === '' && word.word.length > 0;
            if (word.isInserted &&
                (!word.word || (word.word.trim() === '' && !internalWhitespaceMode)) &&
                word.start === prevWordInfo.start &&
                word.end === prevWordInfo.end) {
              // Remove the empty inserted word
              segment.words.splice(wi, 1);
              onTranscriptChange(newData);
              removed = true;
              break;
            }
          }
        }
      }
    }

    // Update the ref for next time
    previousSelectedWordRef.current = selectedWordIndex;
  }, [selectedWordIndex, allWords, data, onTranscriptChange]);

  // This effect runs after a transcript change has caused allWords to be recomputed.
  // It correctly sets the selection and cursor position post-modification.
  useEffect(() => {
    if (nextSelectionInfo) {
      const { newGlobalIndex, cursor } = nextSelectionInfo;
      debugLog('[Transcript] Applying nextSelectionInfo:', {
        newGlobalIndex,
        cursor,
        allWordsLength: allWords.length,
        isInBounds: newGlobalIndex !== null && newGlobalIndex >= 0 && newGlobalIndex < allWords.length
      });
      if (newGlobalIndex !== null && newGlobalIndex >= 0 && newGlobalIndex < allWords.length) {
        setSelectedWordIndex(newGlobalIndex);
        const newWord = allWords[newGlobalIndex];
        setCursorPosition(cursor === 'end' ? (newWord.word || '').length : cursor);
      } else if (newGlobalIndex === allWords.length && allWords.length > 0) {
        // Handle case where index is exactly at the end (last word + 1)
        // This can happen with certain insertions - select the last word instead
        debugLog('[Transcript] Index out of bounds by 1, selecting last word');
        setSelectedWordIndex(allWords.length - 1);
        const lastWord = allWords[allWords.length - 1];
        setCursorPosition(cursor === 'end' ? (lastWord.word || '').length : cursor);
      } else {
        debugLog('[Transcript] newGlobalIndex out of bounds, deselecting');
        setSelectedWordIndex(null);
        setCursorPosition(0);
      }
      setNextSelectionInfo(null); // Reset after applying
      isUpdating.current = false; // Release the lock
      pendingDataRef.current = null; // Clear pending data after update completes
    }
  }, [allWords, nextSelectionInfo]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle paste with Ctrl+V / Cmd+V
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && typingMode) {
        e.preventDefault();

        navigator.clipboard.readText().then(pastedText => {
          if (!pastedText) return;

          const newData = pendingDataRef.current || JSON.parse(JSON.stringify(dataRef.current || data));

          // Handle multi-word selection paste
          if (multiWordSelection) {
            const { startWordIndex, endWordIndex, startOffset, endOffset } = multiWordSelection;
            debugLog('[MultiWordPaste] Pasting into words', startWordIndex, 'to', endWordIndex);

            // First, delete all selected words except the first one
            for (let globalIdx = endWordIndex; globalIdx > startWordIndex; globalIdx--) {
              const wordInfo = allWords[globalIdx];
              if (!wordInfo) continue;

              const { segmentIndex: segIdx, wordIndex: wIdx } = wordInfo;
              const word = newData.segments[segIdx]?.words?.[wIdx];
              if (!word) continue;

              if (word.isInserted) {
                const currentIdx = newData.segments[segIdx].words.indexOf(word);
                if (currentIdx !== -1) {
                  newData.segments[segIdx].words.splice(currentIdx, 1);
                }
              } else {
                word.isGhosted = true;
              }
            }

            // Now handle the first word - replace selection with pasted text
            const startWordInfo = allWords[startWordIndex];
            if (!startWordInfo) {
              console.warn('[MultiWordPaste] Start word not found');
              return;
            }

            const { segmentIndex, wordIndex } = startWordInfo;
            let wordToModify = newData.segments[segmentIndex]?.words?.[wordIndex];
            if (!wordToModify) {
              console.warn('[MultiWordPaste] Word not found in data');
              return;
            }

            const text = wordToModify.word || '';
            const beforeText = text.substring(0, startOffset);
            const afterText = endWordIndex === startWordIndex ? text.substring(endOffset) : '';

            // Ghost original word and create new one if needed
            let newGlobalIndex = startWordIndex;
            if (!wordToModify.isInserted) {
              wordToModify.isGhosted = true;
              const newWord = {
                word: wordToModify.word,
                start: null,
                end: null,
                confidence: null,
                isInserted: true,
                isGhosted: false,
              };
              const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
              newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
              // Assign speaker to the new word based on context
              newWord.speaker = assignSpeakerToNewWord(newData, segmentIndex, insertionPoint);
              wordToModify = newWord;
              const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
              newGlobalIndex = wordsBeforeInsertion;
            }

            // Split pasted text into words and insert
            const pastedWords = pastedText.trim().split(/\s+/);

            if (pastedWords.length === 1) {
              // Single word paste
              wordToModify.word = beforeText + pastedText + afterText;
              setNextSelectionInfo({ newGlobalIndex, cursor: beforeText.length + pastedText.length });
            } else {
              // Multiple words - split current word and insert pasted words between
              wordToModify.word = beforeText + pastedWords[0];

              // Insert middle words
              let insertIndex = wordIndex + 1;
              for (let i = 1; i < pastedWords.length; i++) {
                const newWord = {
                  word: i === pastedWords.length - 1 ? pastedWords[i] + afterText : pastedWords[i],
                  start: null,
                  end: null,
                  confidence: null,
                  isInserted: true,
                  isGhosted: false,
                };
                newData.segments[segmentIndex].words.splice(insertIndex, 0, newWord);
                // Assign speaker to the new word based on context
                newWord.speaker = assignSpeakerToNewWord(newData, segmentIndex, insertIndex);
                insertIndex++;
              }

              // Calculate new position (end of last pasted word)
              const lastWordIndex = wordIndex + pastedWords.length - 1;
              const wordsBeforeLast = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + lastWordIndex;
              const lastWord = newData.segments[segmentIndex].words[lastWordIndex];
              setNextSelectionInfo({ newGlobalIndex: wordsBeforeLast, cursor: lastWord.word.length });
            }

            // Clear multi-word selection
            setMultiWordSelection(null);
            window.getSelection()?.removeAllRanges();

            isUpdating.current = true;
            pendingDataRef.current = newData;
            onTranscriptChange(newData);
            return;
          }

          // Single word paste (existing logic)
          if (selectedWordIndex === null) {
            console.warn('[Paste] No selected word');
            return;
          }

          const wordInfo = allWords[selectedWordIndex];
          if (!wordInfo) {
            console.warn('[Paste] No word info for selectedWordIndex:', selectedWordIndex);
            return;
          }

          const { segmentIndex, wordIndex } = wordInfo;

          if (!newData.segments?.[segmentIndex]?.words?.[wordIndex]) {
            console.warn('[Paste] Word indices are stale:', {
              segmentIndex,
              wordIndex,
              segmentsLength: newData.segments?.length,
              wordsLength: newData.segments?.[segmentIndex]?.words?.length,
              selectedWordIndex
            });
            setSelectedWordIndex(null);
            setCursorPosition(0);
            return;
          }

          const freshWord = newData.segments[segmentIndex].words[wordIndex];
          const text = freshWord.word || '';

          let wordToModify = freshWord;
          let newGlobalIndex = selectedWordIndex;

          // If it's an original word, ghost it and create an editable copy
          if (!wordToModify.isInserted) {
            wordToModify.isGhosted = true;
            const newWord = {
              word: wordToModify.word,
              start: null,
              end: null,
              confidence: null,
              isInserted: true,
              isGhosted: false,
            };
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
            // Assign speaker to the new word based on context
            newWord.speaker = assignSpeakerToNewWord(newData, segmentIndex, insertionPoint);
            wordToModify = newWord;
            const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
            newGlobalIndex = wordsBeforeInsertion;
          }

          // Handle selection replacement
          let start = cursorPosition;
          let end = cursorPosition;
          if (selectionStart !== null && selectionEnd !== null && selectionStart !== selectionEnd) {
            start = Math.min(selectionStart, selectionEnd);
            end = Math.max(selectionStart, selectionEnd);
            setSelectionStart(null);
            setSelectionEnd(null);
          }

          // Split pasted text into words
          const pastedWords = pastedText.trim().split(/\s+/);

          if (pastedWords.length === 1) {
            // Single word paste - insert into current word
            wordToModify.word = text.slice(0, start) + pastedText + text.slice(end);
            setNextSelectionInfo({ newGlobalIndex, cursor: start + pastedText.length });
          } else {
            // Multiple words - split current word and insert pasted words between
            const beforePaste = text.slice(0, start);
            const afterPaste = text.slice(end);

            // First word: combine with text before paste
            wordToModify.word = beforePaste + pastedWords[0];

            // Insert middle words
            let insertIndex = wordIndex + 1;
            for (let i = 1; i < pastedWords.length; i++) {
              const newWord = {
                word: i === pastedWords.length - 1 ? pastedWords[i] + afterPaste : pastedWords[i],
                start: null,
                end: null,
                confidence: null,
                isInserted: true,
                isGhosted: false,
              };
              newData.segments[segmentIndex].words.splice(insertIndex, 0, newWord);
              // Assign speaker to the new word based on context
              newWord.speaker = assignSpeakerToNewWord(newData, segmentIndex, insertIndex);
              insertIndex++;
            }

            // Calculate new position (end of last pasted word)
            const lastWordIndex = wordIndex + pastedWords.length - 1;
            const wordsBeforeLast = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + lastWordIndex;
            const lastWord = newData.segments[segmentIndex].words[lastWordIndex];
            setNextSelectionInfo({ newGlobalIndex: wordsBeforeLast, cursor: lastWord.word.length });
          }

          isUpdating.current = true;
          pendingDataRef.current = newData;
          onTranscriptChange(newData);
        }).catch(err => {
          console.error('Failed to read clipboard:', err);
        });

        return;
      }

      // Handle cut with Ctrl+X / Cmd+X
      if ((e.ctrlKey || e.metaKey) && e.key === 'x' && typingMode) {
        e.preventDefault();

        const newData = pendingDataRef.current || JSON.parse(JSON.stringify(dataRef.current || data));

        // Handle multi-word selection cut
        if (multiWordSelection) {
          const { startWordIndex, endWordIndex, startOffset, endOffset } = multiWordSelection;
          debugLog('[MultiWordCut] Cutting words', startWordIndex, 'to', endWordIndex);

          // First, collect the text to copy to clipboard
          let cutText = '';
          for (let globalIdx = startWordIndex; globalIdx <= endWordIndex; globalIdx++) {
            const wordInfo = allWords[globalIdx];
            if (!wordInfo) continue;

            const { segmentIndex: segIdx, wordIndex: wIdx } = wordInfo;
            const word = newData.segments[segIdx]?.words?.[wIdx];
            if (!word || word.isGhosted) continue;

            if (globalIdx === startWordIndex && globalIdx === endWordIndex) {
              // Single word, partial selection
              cutText = word.word.substring(startOffset, endOffset);
            } else if (globalIdx === startWordIndex) {
              // First word - take from startOffset to end
              cutText += word.word.substring(startOffset);
            } else if (globalIdx === endWordIndex) {
              // Last word - take from beginning to endOffset
              cutText += (cutText ? ' ' : '') + word.word.substring(0, endOffset);
            } else {
              // Middle word - take entire word
              cutText += (cutText ? ' ' : '') + word.word;
            }
          }

          // Copy to clipboard
          navigator.clipboard.writeText(cutText).catch(err => {
            console.error('Failed to write to clipboard:', err);
          });

          // Now delete the selection (same logic as multi-word delete)
          for (let globalIdx = startWordIndex; globalIdx <= endWordIndex; globalIdx++) {
            const wordInfo = allWords[globalIdx];
            if (!wordInfo) continue;

            const { segmentIndex: segIdx, wordIndex: wIdx } = wordInfo;
            const word = newData.segments[segIdx]?.words?.[wIdx];
            if (!word) continue;

            // First word: keep text before startOffset
            if (globalIdx === startWordIndex && startOffset > 0) {
              const beforeText = word.word.substring(0, startOffset);
              if (!word.isInserted) {
                word.isGhosted = true;
                const newWord = {
                  word: beforeText,
                  start: null,
                  end: null,
                  confidence: null,
                  isInserted: true,
                  isGhosted: false
                };
                const insertionPoint = findInsertionPointAfterGhosts(segIdx, wIdx);
                newData.segments[segIdx].words.splice(insertionPoint, 0, newWord);
              } else {
                word.word = beforeText;
              }
            }
            // Last word: keep text after endOffset
            else if (globalIdx === endWordIndex && endOffset < word.word.length) {
              const afterText = word.word.substring(endOffset);
              if (!word.isInserted) {
                word.isGhosted = true;
                const newWord = {
                  word: afterText,
                  start: null,
                  end: null,
                  confidence: null,
                  isInserted: true,
                  isGhosted: false
                };
                const insertionPoint = findInsertionPointAfterGhosts(segIdx, wIdx);
                newData.segments[segIdx].words.splice(insertionPoint, 0, newWord);
              } else {
                word.word = afterText;
              }
            }
            // Middle words or fully selected words: ghost or remove
            else if (globalIdx > startWordIndex && globalIdx < endWordIndex) {
              if (word.isInserted) {
                newData.segments[segIdx].words.splice(wIdx, 1);
              } else {
                word.isGhosted = true;
              }
            }
            // Handle case where start and end are the same word
            else if (globalIdx === startWordIndex && globalIdx === endWordIndex) {
              const beforeText = word.word.substring(0, startOffset);
              const afterText = word.word.substring(endOffset);
              const combinedText = beforeText + afterText;

              if (!word.isInserted) {
                word.isGhosted = true;
                const newWord = {
                  word: combinedText,
                  start: null,
                  end: null,
                  confidence: null,
                  isInserted: true,
                  isGhosted: false
                };
                const insertionPoint = findInsertionPointAfterGhosts(segIdx, wIdx);
                newData.segments[segIdx].words.splice(insertionPoint, 0, newWord);
              } else {
                word.word = combinedText;
              }
            }
          }

          // Clear multi-word selection
          setMultiWordSelectionStart(null);
          setMultiWordSelectionEnd(null);

          // Position cursor at the start of where the cut happened
          const startWordInfo = allWords[startWordIndex];
          if (startWordInfo) {
            const { segmentIndex: segIdx, wordIndex: wIdx } = startWordInfo;
            const wordsBeforeInsertion = newData.segments.slice(0, segIdx).reduce((acc, seg) => acc + seg.words.length, 0) + wIdx;
            setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: startOffset });
          }

          isUpdating.current = true;
          pendingDataRef.current = newData;
          onTranscriptChange(newData);
          return;
        }

        // Single word cut (existing selection within a word)
        if (selectedWordIndex !== null && selectionStart !== null && selectionEnd !== null) {
          const start = Math.min(selectionStart, selectionEnd);
          const end = Math.max(selectionStart, selectionEnd);

          const wordInfo = allWords[selectedWordIndex];
          if (!wordInfo) {
            console.warn('[Cut] Word not found');
            return;
          }

          const { segmentIndex, wordIndex } = wordInfo;
          let wordToModify = newData.segments[segmentIndex].words[wordIndex];
          const text = wordToModify?.word || '';

          // Copy selected text to clipboard
          const cutText = text.slice(start, end);
          navigator.clipboard.writeText(cutText).catch(err => {
            console.error('Failed to write to clipboard:', err);
          });

          // Handle original vs inserted words
          let effectiveText = text;
          let newGlobalIndex = selectedWordIndex;

          if (!wordToModify.isInserted) {
            wordToModify.isGhosted = true;
            const newWord = {
              word: text,
              start: null,
              end: null,
              confidence: null,
              isInserted: true,
              isGhosted: false
            };
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
            wordToModify = newWord;
            effectiveText = newWord.word || '';
            const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
            newGlobalIndex = wordsBeforeInsertion;
          }

          // Delete the selected text
          wordToModify.word = text.slice(0, start) + text.slice(end);

          // Clear selection and set cursor to start of deleted range
          setSelectionStart(null);
          setSelectionEnd(null);
          isUpdating.current = true;
          setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: start });
          pendingDataRef.current = newData;
          onTranscriptChange(newData);
          return;
        }

        return;
      }

      // Don't interfere with typing in input fields, textareas, or other contenteditable elements
      // EXCEPT for the transcript content itself
      // Skip if event is from the mobile hidden input
      if (e.target.classList && e.target.classList.contains('mobile-hidden-input')) {
        // Allow event to be processed even from mobile input
      } else if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' ||
          (e.target.contentEditable === 'true' && e.target !== transcriptContentRef.current)) {
        return;
      }

      // Don't prevent events when isUpdating - let the effectiveText logic handle it
      // This prevents lost keystrokes during rapid typing

      if (!typingMode || selectedWordIndex === null) {
        // Handle space key to toggle play/pause in playing mode
        if (!typingMode && e.key === ' ') {
          e.preventDefault();
          if (audioRef && audioRef.current) {
            if (audioRef.current.paused) {
              audioRef.current.play();
            } else {
              audioRef.current.pause();
            }
          }
          return;
        }

        // Handle listening mode navigation
        if (!typingMode && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
          e.preventDefault();

          // With bin navigation enabled, arrow keys navigate within confidence bins
          // Without it, arrow keys navigate word by word (Shift+Arrow still does bin navigation)
          const useBinNav = enableBinNavigation ? !e.shiftKey : e.shiftKey;

          if (useBinNav) {
            // Bin navigation
            let startNavIndex = selectedWordIndex ?? activeWordGlobalIndex ?? 0;
            if (startNavIndex !== null) {
              setSelectedWordIndex(startNavIndex);
              navigateInBin(e.key === 'ArrowRight' ? 'next' : 'prev', startNavIndex);
            }
          } else {
            // Word-by-word navigation
            let currentIndex = selectedWordIndex ?? activeWordGlobalIndex ?? 0;
            const direction = e.key === 'ArrowRight' ? 'next' : 'prev';
            const newIndex = findNavigableWord(currentIndex, direction);

            if (newIndex !== null) {
              setSelectedWordIndex(newIndex);
              // In listening mode, also seek audio to the new word
              if (onTokenClick) {
                const newWord = allWords[newIndex];
                if (newWord && newWord.start != null) {
                  onTokenClick(newWord.start + 0.01);
                }
              }
            }
          }
        }
        return;
      }

      // Use pending data if available (during rapid typing), otherwise clone from current data
      // This prevents lost keystrokes when typing faster than React can re-render
      // IMPORTANT: Get fresh data FIRST before computing wordInfo to avoid race conditions
      const newData = pendingDataRef.current
        ? JSON.parse(JSON.stringify(pendingDataRef.current))
        : JSON.parse(JSON.stringify(dataRef.current || data));

      // Recalculate word info from fresh data instead of using stale allWords
      // This prevents "Word indices are stale" errors during rapid typing
      const getWordInfoFromData = (data, globalIndex) => {
        if (!data || !data.segments) return null;

        let currentGlobalIndex = 0;
        for (let segIdx = 0; segIdx < data.segments.length; segIdx++) {
          const segment = data.segments[segIdx];
          if (!segment || !segment.words) continue;

          for (let wordIdx = 0; wordIdx < segment.words.length; wordIdx++) {
            if (currentGlobalIndex === globalIndex) {
              return {
                word: segment.words[wordIdx],
                segmentIndex: segIdx,
                wordIndex: wordIdx,
                globalIndex: currentGlobalIndex
              };
            }
            currentGlobalIndex++;
          }
        }
        return null;
      };

      const wordInfo = getWordInfoFromData(newData, selectedWordIndex);
      if (!wordInfo) {
        console.warn('[Keyboard] No word info found for selectedWordIndex:', {
          selectedWordIndex,
          totalWords: allWords.length
        });
        // Reset selection to prevent further errors
        setSelectedWordIndex(null);
        setCursorPosition(0);
        return;
      }

      const { segmentIndex, wordIndex, word: freshWord } = wordInfo;
      const text = freshWord.word || '';
      const atStart = cursorPosition === 0;
      const atEnd = cursorPosition === text.length;

      // Only prevent default for keys we actually handle in typing mode
      const handledKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace', 'Delete', ' ', 'Escape', 'Tab', 'Enter'];
      const isHandledKey = handledKeys.includes(e.key) || (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey);

      if (!isHandledKey) {
        return; // Let the browser handle unrecognized keys
      }

      // Only prevent default if we're in typing mode and have a selected word
      if (typingMode && selectedWordIndex !== null) {
        e.preventDefault();
      }

      // --- Enter Key: Always insert newline in typing mode ---
      if (e.key === 'Enter') {
        e.preventDefault();

        const wordToEdit = freshWord;
        const currentText = wordToEdit.word || '';
        const beforeCursor = currentText.slice(0, cursorPosition);
        const afterCursor = currentText.slice(cursorPosition);

        // Insert newline character at cursor position
        const newText = beforeCursor + '\n' + afterCursor;
        const newCursorPos = cursorPosition + 1;

        // Update the word
        newData.segments[segmentIndex].words[wordIndex].word = newText;

        setNextSelectionInfo({ newGlobalIndex: selectedWordIndex, cursor: newCursorPos });
        onTranscriptChange(newData);
        return;
      }

      // --- Shift+Tab: Insert tab character ---
      if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();

        const wordToEdit = freshWord;
        const currentText = wordToEdit.word || '';
        const beforeCursor = currentText.slice(0, cursorPosition);
        const afterCursor = currentText.slice(cursorPosition);

        // Insert tab character at cursor position
        const newText = beforeCursor + '\t' + afterCursor;
        const newCursorPos = cursorPosition + 1;

        // Update the word
        newData.segments[segmentIndex].words[wordIndex].word = newText;

        setNextSelectionInfo({ newGlobalIndex: selectedWordIndex, cursor: newCursorPos });
        onTranscriptChange(newData);
        return;
      }

      // --- Tab Key: Always toggle suggestions (regardless of suggestions mode) ---
      if (e.key === 'Tab') {
        e.preventDefault();

        // Check if word has alternatives, fetch if not
        const alternatives = freshWord.alternatives || [];
        debugLog('[Tab] Tab pressed. Word:', freshWord.word, 'Alternatives:', alternatives.length, 'isInserted:', freshWord.isInserted);

        if (alternatives.length === 0 && !freshWord.isInserted && transcriptId) {
          // Skip alternatives for unauthenticated users
          const authToken = localStorage.getItem('authToken');
          if (!authToken) {
            debugLog('[Tab] Skipping alternatives: no auth token');
          } else {
            // Use ORIGINAL indices for backend API (not processed segment indices)
            const origSegIdx = wordInfo.originalSegmentIndex ?? segmentIndex;
            const origWordIdx = wordInfo.originalWordIndex ?? wordIndex;
            debugLog('[Tab] Fetching alternatives for word:', freshWord.word, 'originalIndices:', { origSegIdx, origWordIdx });

            // Fetch alternatives on-demand
            fetch(`/api/transcripts/${transcriptId}/batch-word-alternatives`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              words: [{
                word: freshWord.word,
                segmentIndex: origSegIdx,
                wordIndex: origWordIdx,
                confidence: freshWord.confidence ?? freshWord.probability ?? 0
              }]
            })
          }).then(response => {
            if (!response.ok) {
              console.error('[Tab] Failed to fetch alternatives:', response.status);
              return;
            }
            // Process SSE stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            function processStream() {
              reader.read().then(({ done, value }) => {
                if (done) {
                  return;
                }

                try {
                  const text = decoder.decode(value);
                  const lines = text.split('\n');

                  for (const line of lines) {
                    if (line.startsWith('data: ')) {
                      try {
                        const data = JSON.parse(line.substring(6));

                        if (data.segmentIndex != null && data.wordIndex != null && data.alternatives) {
                          // Update the word with alternatives
                          onTranscriptChange(prevData => {
                            if (!prevData || !prevData.segments) {
                              console.warn('[Tab] Invalid prevData, skipping update');
                              return prevData;
                            }
                            const newData = JSON.parse(JSON.stringify(prevData));
                            if (newData.segments[data.segmentIndex]?.words[data.wordIndex]) {
                              newData.segments[data.segmentIndex].words[data.wordIndex].alternatives = data.alternatives;
                              debugLog('[Tab] Alternatives loaded:', data.alternatives.length);
                            }
                            return newData;
                          });

                          // Show suggestions after loading
                          setShowSuggestions(true);
                          setSelectedSuggestionIndex(0);
                        }
                      } catch (e) {
                        console.error('[Tab] Failed to parse SSE:', e);
                      }
                    }
                  }
                } catch (decodeError) {
                  console.error('[Tab] Error decoding SSE stream:', decodeError);
                }

                processStream(); // Continue reading
              }).catch(streamError => {
                console.error('[Tab] Error reading SSE stream:', streamError);
              });
            }

            processStream();
          }).catch(err => {
            console.error('[Tab] Error fetching alternatives:', err);
          });
          }
        } else {
          // Toggle suggestions dropdown for current word
          setShowSuggestions(prev => !prev);
          setSelectedSuggestionIndex(0); // Reset selection when toggling
        }
        return;
      }

      // Handle arrow keys for suggestion navigation
      if (showSuggestions && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const alternatives = freshWord.alternatives || [];
        if (alternatives.length > 0) {
          if (e.key === 'ArrowDown') {
            setSelectedSuggestionIndex(prev => Math.min(prev + 1, alternatives.length - 1));
          } else {
            setSelectedSuggestionIndex(prev => Math.max(prev - 1, 0));
          }
        }
        return;
      }

      // Handle Enter to accept selected suggestion
      if (showSuggestions && e.key === 'Enter') {
        e.preventDefault();
        const alternatives = freshWord.alternatives || [];
        if (alternatives.length > 0 && selectedSuggestionIndex < alternatives.length) {
          const selectedAlt = alternatives[selectedSuggestionIndex];
          // Replace word with selected alternative
          let wordToModify = newData.segments[segmentIndex].words[wordIndex];
          let newGlobalIndex = selectedWordIndex;

          if (!wordToModify.isInserted) {
            wordToModify.isGhosted = true;
            const newWord = {
              word: selectedAlt.word,
              start: null,
              end: null,
              confidence: null,
              isInserted: true,
              isGhosted: false,
            };
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
            const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
            newGlobalIndex = wordsBeforeInsertion;
          } else {
            wordToModify.word = selectedAlt.word;
          }

          setShowSuggestions(false);
          setSelectedSuggestionIndex(0);
          setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: selectedAlt.word.length });
          onTranscriptChange(newData);
        }
        return;
      }

      // Close suggestions on Escape
      if (e.key === 'Escape' && showSuggestions) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(0);
        return;
      }

      // --- Text Selection: Shift+Arrow Keys ---
      if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();

        // Initialize selection if not already selecting
        if (selectionStart === null) {
          setSelectionStart(cursorPosition);
        }

        // Move cursor and extend selection
        const newCursor = e.key === 'ArrowLeft' ?
          Math.max(0, cursorPosition - 1) :
          Math.min(text.length, cursorPosition + 1);
        setCursorPosition(newCursor);
        setSelectionEnd(newCursor);
        return;
      }

      // Clear selection if arrow keys without shift
      if (!e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        setSelectionStart(null);
        setSelectionEnd(null);
      }

      // --- Ctrl+Delete: Delete entire word ---
      if ((e.ctrlKey || e.metaKey) && e.key === 'Delete') {
        e.preventDefault();

        // Ghost the current word if it's original, or delete it if it's inserted
        let wordToModify = newData.segments[segmentIndex].words[wordIndex];
        if (!wordToModify.isInserted) {
          wordToModify.isGhosted = true;
        } else {
          newData.segments[segmentIndex].words.splice(wordIndex, 1);
        }

        // Move to next word or previous word
        const nextWordIndex = findNavigableWord(selectedWordIndex, 'next');
        const targetIndex = nextWordIndex !== null ? nextWordIndex : findNavigableWord(selectedWordIndex, 'prev');

        if (targetIndex !== null) {
          setNextSelectionInfo({ newGlobalIndex: targetIndex, cursor: 0 });
        } else {
          setSelectedWordIndex(null);
          setCursorPosition(0);
        }

        onTranscriptChange(newData);
        return;
      }

      // --- Text Modification: Backspace & Delete ---
      if (e.key === 'Backspace' || e.key === 'Delete') {
        // Handle multi-word selection deletion
        if (multiWordSelection) {
          e.preventDefault();
          const { startWordIndex, endWordIndex, startOffset, endOffset } = multiWordSelection;

          debugLog('[MultiWordDelete] Deleting words', startWordIndex, 'to', endWordIndex);

          // Process all words in the selection
          for (let globalIdx = startWordIndex; globalIdx <= endWordIndex; globalIdx++) {
            const wordInfo = allWords[globalIdx];
            if (!wordInfo) continue;

            const { segmentIndex: segIdx, wordIndex: wIdx } = wordInfo;
            const word = newData.segments[segIdx]?.words?.[wIdx];
            if (!word) continue;

            // First word: keep text before startOffset
            if (globalIdx === startWordIndex && startOffset > 0) {
              const beforeText = word.word.substring(0, startOffset);
              if (!word.isInserted) {
                word.isGhosted = true;
                const newWord = {
                  word: beforeText,
                  start: null,
                  end: null,
                  confidence: null,
                  isInserted: true,
                  isGhosted: false,
                };
                const insertionPoint = findInsertionPointAfterGhosts(segIdx, wIdx);
                newData.segments[segIdx].words.splice(insertionPoint, 0, newWord);
              } else {
                word.word = beforeText;
              }
            }
            // Last word: keep text after endOffset
            else if (globalIdx === endWordIndex && endOffset < (word.word?.length || 0)) {
              const afterText = word.word.substring(endOffset);
              if (!word.isInserted) {
                word.isGhosted = true;
                const newWord = {
                  word: afterText,
                  start: null,
                  end: null,
                  confidence: null,
                  isInserted: true,
                  isGhosted: false,
                };
                const insertionPoint = findInsertionPointAfterGhosts(segIdx, wIdx);
                newData.segments[segIdx].words.splice(insertionPoint, 0, newWord);
              } else {
                word.word = afterText;
              }
            }
            // Middle words: delete/ghost entirely
            else {
              if (word.isInserted) {
                // Find current index in array (may have shifted)
                const currentIdx = newData.segments[segIdx].words.indexOf(word);
                if (currentIdx !== -1) {
                  newData.segments[segIdx].words.splice(currentIdx, 1);
                }
              } else {
                word.isGhosted = true;
              }
            }
          }

          // Clear multi-word selection and set cursor to start
          setMultiWordSelection(null);
          window.getSelection()?.removeAllRanges();

          // Set cursor to the start word
          const startWordInfo = allWords[startWordIndex];
          if (startWordInfo) {
            setNextSelectionInfo({ newGlobalIndex: startWordIndex, cursor: startOffset });
          }

          isUpdating.current = true;
          pendingDataRef.current = newData;
          onTranscriptChange(newData);
          return;
        }

        // If there's a selection within a single word, delete the selected text
        if (selectionStart !== null && selectionEnd !== null && selectionStart !== selectionEnd) {
          const start = Math.min(selectionStart, selectionEnd);
          const end = Math.max(selectionStart, selectionEnd);

          let wordToModify = newData.segments[segmentIndex].words[wordIndex];
          let newGlobalIndex = selectedWordIndex;

          // If it's an original word, ghost it and create an editable copy
          if (!wordToModify.isInserted) {
            wordToModify.isGhosted = true;
            const newWord = {
              word: wordToModify.word,
              start: null,
              end: null,
              confidence: null,
              isInserted: true,
              isGhosted: false,
            };
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
            // Assign speaker to the new word based on context
            newWord.speaker = assignSpeakerToNewWord(newData, segmentIndex, insertionPoint);
            wordToModify = newWord;
            const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
            newGlobalIndex = wordsBeforeInsertion;
          }

          // Delete the selected text
          wordToModify.word = text.slice(0, start) + text.slice(end);

          // Clear selection and set cursor to start of deleted range
          setSelectionStart(null);
          setSelectionEnd(null);
          isUpdating.current = true;
          setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: start });
          pendingDataRef.current = newData;
          onTranscriptChange(newData);
          return;
        }
        // Note: Whitespace (tabs, newlines) within words should be deletable like normal characters
        // Only join words when deleting at word boundaries, not when deleting whitespace
        // This allows users to use tabs and newlines for formatting without word joining

        // Case 1: Deleting a character inside a word.
        if ((e.key === 'Backspace' && !atStart) || (e.key === 'Delete' && !atEnd)) {
          // Safety check for segment and word existence
          if (!newData.segments ||
              !newData.segments[segmentIndex] ||
              !newData.segments[segmentIndex].words ||
              !newData.segments[segmentIndex].words[wordIndex]) {
            console.warn('Word not found for editing:', {
              segmentIndex,
              wordIndex,
              segmentsLength: newData.segments?.length,
              wordsLength: newData.segments?.[segmentIndex]?.words?.length,
              selectedWordIndex,
              totalWords: allWords.length
            });
            // Try to recover by refreshing the word selection
            setSelectedWordIndex(null);
            setCursorPosition(0);
            return;
          }

          let wordToModify = newData.segments[segmentIndex].words[wordIndex];
          let newGlobalIndex = selectedWordIndex;
          let newCursorPos = cursorPosition;

          // Use the latest text if we're in the middle of an update
          let effectiveText = text;
          let effectiveCursorPosition = cursorPosition;

          if (isUpdating.current && newData.segments[segmentIndex].words[wordIndex]) {
            const latestWord = newData.segments[segmentIndex].words[wordIndex];
            effectiveText = latestWord.word || '';
          }

          if (nextSelectionInfo && isUpdating.current) {
            effectiveCursorPosition = nextSelectionInfo.cursor === 'end' ? effectiveText.length : nextSelectionInfo.cursor;
          } else if (nextSelectionInfo && nextSelectionInfo.newGlobalIndex === selectedWordIndex) {
            effectiveCursorPosition = nextSelectionInfo.cursor === 'end' ? effectiveText.length : nextSelectionInfo.cursor;
          }

          // If it's an original word, ghost it and create an editable copy.
          if (!wordToModify.isInserted) {
            wordToModify.isGhosted = true;
            const newWord = {
              word: wordToModify.word, start: null, end: null, confidence: null,
              isInserted: true, isGhosted: false,
            };
            // Use helper function to find correct insertion point after any ghosted words
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
            wordToModify = newWord; // The new word is now our target
            effectiveText = wordToModify.word || '';
            // Calculate the new global index based on the insertion point
            const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
            newGlobalIndex = wordsBeforeInsertion;
          }

          // Now, modify the word (which is guaranteed to be an inserted one).
          if (e.key === 'Backspace') {
            wordToModify.word = effectiveText.slice(0, effectiveCursorPosition - 1) + effectiveText.slice(effectiveCursorPosition);
            newCursorPos = effectiveCursorPosition - 1;
          } else { // Delete
            wordToModify.word = effectiveText.slice(0, effectiveCursorPosition) + effectiveText.slice(effectiveCursorPosition + 1);
            newCursorPos = effectiveCursorPosition;
          }

          // If the word becomes empty or only whitespace, remove it (unless whitespace mode is on).
          const isWhitespaceOnly = wordToModify.word && wordToModify.word.trim() === '' && wordToModify.word.length > 0;
          if ((!wordToModify.word || (wordToModify.word.trim() === '' && !internalWhitespaceMode)) && wordToModify.isInserted) {
            const currentLocalIndex = newData.segments[segmentIndex].words.indexOf(wordToModify);
            newData.segments[segmentIndex].words.splice(currentLocalIndex, 1);
            const prevWordIndex = findNavigableWord(selectedWordIndex, 'prev');
            setNextSelectionInfo({ newGlobalIndex: prevWordIndex, cursor: 'end' });
          } else {
            isUpdating.current = true;
            setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: newCursorPos });
          }
          pendingDataRef.current = newData; // Store for next rapid keystroke
          onTranscriptChange(newData);
          return;
        }

        // Case 2: Deleting an entire word or joining words (cursor at edge).
        if ((e.key === 'Backspace' && atStart) || (e.key === 'Delete' && atEnd)) {
          // Try to join with adjacent word first
          if (e.key === 'Backspace' && atStart) {
            const prevWordIndex = findNavigableWord(selectedWordIndex, 'prev');
            if (prevWordIndex !== null) {
              const prevWordInfo = allWords[prevWordIndex];
              // Get fresh word data from newData
              const freshPrevWord = newData.segments[prevWordInfo.segmentIndex].words[prevWordInfo.wordIndex];
              // Don't trim whitespace - preserve tabs and newlines
              const joinedWord = (freshPrevWord.word || '') + (freshWord.word || '');
              
              // Handle removal in the correct order to avoid stale indices
              // If words are in different segments, handle them separately
              if (prevWordInfo.segmentIndex === segmentIndex) {
                // Same segment - handle carefully to avoid index shifting issues
                const higherIndex = Math.max(prevWordInfo.wordIndex, wordIndex);
                const lowerIndex = Math.min(prevWordInfo.wordIndex, wordIndex);

                // Count how many words we actually remove (not ghost) to adjust insertion index
                let removedCount = 0;

                // Remove/ghost higher index word first (to avoid shifting lower index)
                const higherWord = newData.segments[segmentIndex].words[higherIndex];
                if (higherWord?.isInserted) {
                  newData.segments[segmentIndex].words.splice(higherIndex, 1);
                  removedCount++;
                } else if (higherWord) {
                  higherWord.isGhosted = true;
                }

                // Remove/ghost lower index word (index unchanged since we removed higher first)
                const lowerWord = newData.segments[segmentIndex].words[lowerIndex];
                if (lowerWord?.isInserted) {
                  newData.segments[segmentIndex].words.splice(lowerIndex, 1);
                  removedCount++;
                } else if (lowerWord) {
                  lowerWord.isGhosted = true;
                }

                // Insert new joined word
                // If we removed both words, insert at lowerIndex
                // If we removed only higher word, insert at lowerIndex (unchanged)
                // If we ghosted both, insert at lowerIndex
                const newWord = {
                  word: joinedWord, start: null, end: null, confidence: null,
                  isInserted: true, isGhosted: false,
                };
                newData.segments[segmentIndex].words.splice(lowerIndex, 0, newWord);

                const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + lowerIndex;
                setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: (freshPrevWord.word || '').length });
              } else {
                // Different segments - handle each separately
                // Ghost/remove the previous word
                const prevWord = newData.segments[prevWordInfo.segmentIndex]?.words?.[prevWordInfo.wordIndex];
                if (prevWord?.isInserted) {
                  newData.segments[prevWordInfo.segmentIndex].words.splice(prevWordInfo.wordIndex, 1);
                } else if (prevWord) {
                  prevWord.isGhosted = true;
                }
                
                // Ghost/remove the current word
                const currentWord = newData.segments[segmentIndex]?.words?.[wordIndex];
                if (currentWord?.isInserted) {
                  newData.segments[segmentIndex].words.splice(wordIndex, 1);
                } else if (currentWord) {
                  currentWord.isGhosted = true;
                }
                
                // Insert new joined word at the previous word's position
                const newWord = {
                  word: joinedWord, start: null, end: null, confidence: null,
                  isInserted: true, isGhosted: false,
                };

                const insertionPoint = findInsertionPointAfterGhosts(prevWordInfo.segmentIndex, prevWordInfo.wordIndex);
                newData.segments[prevWordInfo.segmentIndex].words.splice(insertionPoint, 0, newWord);

                const wordsBeforeInsertion = newData.segments.slice(0, prevWordInfo.segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
                setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: (freshPrevWord.word || '').length });
              }
              
              onTranscriptChange(newData);
              return;
            }
          }

          if (e.key === 'Delete' && atEnd) {
            const nextWordIndex = findNavigableWord(selectedWordIndex, 'next');
            if (nextWordIndex !== null) {
              const nextWordInfo = allWords[nextWordIndex];
              // Get fresh word data from newData
              const freshNextWord = newData.segments[nextWordInfo.segmentIndex].words[nextWordInfo.wordIndex];
              // Don't trim whitespace - preserve tabs and newlines
              const joinedWord = (freshWord.word || '') + (freshNextWord.word || '');
              
              // Handle removal in the correct order to avoid stale indices
              // If words are in different segments, handle them separately
              if (segmentIndex === nextWordInfo.segmentIndex) {
                // Same segment - handle higher index first to avoid shifting
                const higherIndex = Math.max(wordIndex, nextWordInfo.wordIndex);
                const lowerIndex = Math.min(wordIndex, nextWordInfo.wordIndex);
                
                // Remove/ghost higher index word first
                const higherWord = newData.segments[segmentIndex].words[higherIndex];
                if (higherWord?.isInserted) {
                  newData.segments[segmentIndex].words.splice(higherIndex, 1);
                } else if (higherWord) {
                  higherWord.isGhosted = true;
                }
                
                // Remove/ghost lower index word
                const lowerWord = newData.segments[segmentIndex].words[lowerIndex];
                if (lowerWord?.isInserted) {
                  newData.segments[segmentIndex].words.splice(lowerIndex, 1);
                } else if (lowerWord) {
                  lowerWord.isGhosted = true;
                }
                
                // Insert new joined word at the lower position
                const newWord = {
                  word: joinedWord, start: null, end: null, confidence: null,
                  isInserted: true, isGhosted: false,
                };
                newData.segments[segmentIndex].words.splice(lowerIndex, 0, newWord);
                
                const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + lowerIndex;
                setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: (text || '').length });
              } else {
                // Different segments - handle each separately
                // Ghost/remove the current word
                const currentWord = newData.segments[segmentIndex]?.words?.[wordIndex];
                if (currentWord?.isInserted) {
                  newData.segments[segmentIndex].words.splice(wordIndex, 1);
                } else if (currentWord) {
                  currentWord.isGhosted = true;
                }
                
                // Ghost/remove the next word
                const nextWord = newData.segments[nextWordInfo.segmentIndex]?.words?.[nextWordInfo.wordIndex];
                if (nextWord?.isInserted) {
                  newData.segments[nextWordInfo.segmentIndex].words.splice(nextWordInfo.wordIndex, 1);
                } else if (nextWord) {
                  nextWord.isGhosted = true;
                }
                
                // Insert new joined word at the current word's position
                const newWord = {
                  word: joinedWord, start: null, end: null, confidence: null,
                  isInserted: true, isGhosted: false,
                };
                
                const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
                newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
                
                const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
                setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: (text || '').length });
              }
              
              onTranscriptChange(newData);
              return;
            }
          }

          // Only if no adjacent word to join with, then delete/ghost the current word
          const wordToModify = newData.segments[segmentIndex]?.words?.[wordIndex];
          if (wordToModify) {
            if (wordToModify.isInserted) {
              newData.segments[segmentIndex].words.splice(wordIndex, 1);
              const nextGlobalIndex = findNavigableWord(selectedWordIndex, e.key === 'Backspace' ? 'prev' : 'next');
              setNextSelectionInfo({ newGlobalIndex: nextGlobalIndex, cursor: e.key === 'Backspace' ? 'end' : 0 });
            } else {
              wordToModify.isGhosted = true;
              const newWord = {
                word: '', start: null, end: null, confidence: null,
                isInserted: true, isGhosted: false,
              };
              
              const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
              newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
              
              const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
              setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: 0 });
            }
          }
          
          onTranscriptChange(newData);
          return;
        }
      }
      
      // --- Text Modification: Character Input ---
      if (e.key === ' ') {
        if (!atStart && !atEnd) {
          // Word splitting logic - space in middle of word
          const firstPart = text.slice(0, cursorPosition);
          const secondPart = text.slice(cursorPosition);

          // Safety check for segment and word existence
          if (!newData.segments[segmentIndex] || !newData.segments[segmentIndex].words[wordIndex]) {
            console.warn('Word not found for split operation:', { segmentIndex, wordIndex });
            return;
          }

          let wordToModify = newData.segments[segmentIndex].words[wordIndex];
          let newGlobalIndex = selectedWordIndex;

          if (!wordToModify.isInserted) {
            wordToModify.isGhosted = true;
            const newWord = {
              word: firstPart, start: null, end: null, confidence: null,
              isInserted: true, isGhosted: false,
            };
            // Use helper function to find correct insertion point after any ghosted words
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
            wordToModify = newWord;
            // Calculate the new global index based on the insertion point
            const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
            newGlobalIndex = wordsBeforeInsertion;
          } else {
            wordToModify.word = firstPart;
          }

          const newWord = {
            word: secondPart, start: null, end: null, confidence: null,
            isInserted: true, isGhosted: false,
          };
          // Insert the second part right after the first part
          // Use the known insertion point instead of indexOf to avoid issues with duplicates or modified words
          let secondWordInsertionPoint;
          if (!newData.segments[segmentIndex].words[wordIndex].isInserted) {
            // If we ghosted the original word, the new word was inserted after it
            const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
            secondWordInsertionPoint = insertionPoint + 1;
          } else {
            // If we modified an existing inserted word, insert right after it
            secondWordInsertionPoint = wordIndex + 1;
          }
          newData.segments[segmentIndex].words.splice(secondWordInsertionPoint, 0, newWord);
          
          // Calculate global index for the second word
          const wordsBeforeSecondInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + secondWordInsertionPoint;
          isUpdating.current = true;
          setNextSelectionInfo({ newGlobalIndex: wordsBeforeSecondInsertion, cursor: 0 });
          pendingDataRef.current = newData; // Store for next rapid keystroke
          onTranscriptChange(newData);
          return;
        } else if (atEnd) {
          // Space at end of word - create new empty word
          const newWord = {
            word: '', start: null, end: null, confidence: null,
            isInserted: true, isGhosted: false,
          };
          
          // Insert new word after current word
          const insertionPoint = wordIndex + 1;
          newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
          
          // Calculate global index for the new word
          const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
          isUpdating.current = true;
          setNextSelectionInfo({ newGlobalIndex: wordsBeforeInsertion, cursor: 0 });
          pendingDataRef.current = newData; // Store for next rapid keystroke
          onTranscriptChange(newData);
          return;
        }
        // If space at start, do nothing (ignore)
        return;
      }

      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        // Safety check for segment and word existence
        if (!newData.segments[segmentIndex] || !newData.segments[segmentIndex].words[wordIndex]) {
          console.warn('Word not found for character input:', { segmentIndex, wordIndex });
          return;
        }

        let wordToModify = newData.segments[segmentIndex].words[wordIndex];
        let newGlobalIndex = selectedWordIndex;

        // Use pending cursor position and text if available to avoid race condition
        let effectiveCursorPosition = cursorPosition;
        let effectiveText = text;

        // If we're in the middle of an update (rapid typing), use the latest modified word's text
        if (isUpdating.current && newData.segments[segmentIndex].words[wordIndex]) {
          // Get the most recent version of the word from newData
          const latestWord = newData.segments[segmentIndex].words[wordIndex];
          effectiveText = latestWord.word || '';
        }

        // If we're in the middle of an update (rapid typing), always use pending cursor position
        if (nextSelectionInfo && isUpdating.current) {
          effectiveCursorPosition = nextSelectionInfo.cursor === 'end' ? effectiveText.length : nextSelectionInfo.cursor;
        } else if (nextSelectionInfo && nextSelectionInfo.newGlobalIndex === selectedWordIndex) {
          effectiveCursorPosition = nextSelectionInfo.cursor === 'end' ? effectiveText.length : nextSelectionInfo.cursor;
        }

        // If it's an original word, ghost it and create an editable copy.
        if (!wordToModify.isInserted) {
          wordToModify.isGhosted = true;
          const newWord = {
            word: wordToModify.word, start: null, end: null, confidence: null,
            isInserted: true, isGhosted: false,
          };
          // Use helper function to find correct insertion point after any ghosted words
          const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
          newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
          wordToModify = newWord; // The new word is now our target
          effectiveText = wordToModify.word || ''; // Update effective text after creating copy
          // Calculate the new global index based on the insertion point
          const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
          newGlobalIndex = wordsBeforeInsertion;
        }

        // If there's a selection, replace it with the typed character
        if (selectionStart !== null && selectionEnd !== null && selectionStart !== selectionEnd) {
          const start = Math.min(selectionStart, selectionEnd);
          const end = Math.max(selectionStart, selectionEnd);
          wordToModify.word = effectiveText.slice(0, start) + e.key + effectiveText.slice(end);
          const newCursorPos = start + 1;

          // Clear selection
          setSelectionStart(null);
          setSelectionEnd(null);

          isUpdating.current = true;
          isTypingChange.current = true;
          setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: newCursorPos });
          pendingDataRef.current = newData;
          onTranscriptChange(newData);
          return;
        }

        // Now, modify the word (which is guaranteed to be an inserted one).
        wordToModify.word = effectiveText.slice(0, effectiveCursorPosition) + e.key + effectiveText.slice(effectiveCursorPosition);
        const newCursorPos = effectiveCursorPosition + 1;

        isUpdating.current = true;
        isTypingChange.current = true; // Mark as typing change to prevent scroll
        setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: newCursorPos });
        pendingDataRef.current = newData; // Store for next rapid keystroke
        onTranscriptChange(newData);
        return;
      }
      
      // --- Command/Ctrl + Arrow: Jump by word ---
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        const direction = e.key === 'ArrowLeft' ? 'prev' : e.key === 'ArrowRight' ? 'next' : null;
        if (direction) {
          const newIndex = findNavigableWord(selectedWordIndex, direction);
          if (newIndex !== null) {
            setSelectedWordIndex(newIndex);
            const newWord = allWords[newIndex];
            // For forward navigation, go to start of word; for backward, go to end
            setCursorPosition(direction === 'next' ? 0 : (newWord.word || '').length);
            setShowSuggestions(false); // Close suggestions when navigating
            setSelectedSuggestionIndex(0);
          }
        }
        return;
      }

      // Only handle ArrowLeft/ArrowRight for navigation if suggestions are NOT open
      if (e.key === 'ArrowLeft' && !showSuggestions) {
        if (!atStart) setCursorPosition(c => c - 1);
        else {
          const prevIndex = findNavigableWord(selectedWordIndex, 'prev');
          if (prevIndex !== null) {
            setSelectedWordIndex(prevIndex);
            setCursorPosition((allWords[prevIndex].word || '').length);
          }
        }
      } else if (e.key === 'ArrowRight' && !showSuggestions) {
        if (!atEnd) setCursorPosition(c => c + 1);
        else {
          const nextIndex = findNavigableWord(selectedWordIndex, 'next');
          if (nextIndex !== null) {
            setSelectedWordIndex(nextIndex);
            setCursorPosition(0);
          }
        }
      } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && !showSuggestions) {
        // Vertical navigation in typing mode
        e.preventDefault();

        // Get current word's position
        const currentWordRef = wordRefs.current[selectedWordIndex];
        if (!currentWordRef?.current) return;

        const currentRect = currentWordRef.current.getBoundingClientRect();
        const currentCenterY = currentRect.top + currentRect.height / 2;

        // Calculate approximate cursor X position within the word
        const wordElement = currentWordRef.current.querySelector('.token-text');
        if (!wordElement) return;

        // Create a range to measure cursor position
        let cursorX = currentRect.left;
        try {
          const range = document.createRange();
          const textNode = wordElement.firstChild;
          if (textNode && textNode.nodeType === Node.TEXT_NODE && cursorPosition <= text.length) {
            range.setStart(textNode, 0);
            range.setEnd(textNode, Math.min(cursorPosition, text.length));
            const rangeRect = range.getBoundingClientRect();
            cursorX = rangeRect.right;
          }
        } catch (err) {
          // Fallback to word start position
          cursorX = currentRect.left;
        }

        // Find all navigable words above or below
        const direction = e.key === 'ArrowUp' ? -1 : 1;
        const candidateWords = [];

        for (let i = 0; i < allWords.length; i++) {
          if (i === selectedWordIndex) continue;
          if (allWords[i].isGhosted) continue;

          const wordRef = wordRefs.current[i];
          if (!wordRef?.current) continue;

          const rect = wordRef.current.getBoundingClientRect();
          const centerY = rect.top + rect.height / 2;

          // Check if word is in the right direction (above or below)
          const isInDirection = direction < 0 ? (centerY < currentCenterY) : (centerY > currentCenterY);
          if (!isInDirection) continue;

          // Calculate distance
          const yDistance = Math.abs(centerY - currentCenterY);
          const xDistance = Math.abs((rect.left + rect.right) / 2 - cursorX);

          candidateWords.push({
            index: i,
            yDistance,
            xDistance,
            rect
          });
        }

        if (candidateWords.length > 0) {
          // Sort by Y distance first (find closest line), then by X distance (find closest horizontally)
          candidateWords.sort((a, b) => {
            const yDiff = a.yDistance - b.yDistance;
            if (Math.abs(yDiff) > 5) return yDiff; // Different lines
            return a.xDistance - b.xDistance; // Same line, prefer closest horizontally
          });

          const targetWord = candidateWords[0];
          setSelectedWordIndex(targetWord.index);

          // Try to maintain relative cursor position
          const targetWordInfo = allWords[targetWord.index];
          const targetWordLength = (targetWordInfo.word || '').length;
          const relativeCursorPos = Math.min(cursorPosition, targetWordLength);
          setCursorPosition(relativeCursorPos);
        }
      } else if (e.key === 'Escape') {
        if (showSuggestions) {
          setShowSuggestions(false);
          setSelectedSuggestionIndex(0);
        } else {
          setSelectedWordIndex(null);
          setCursorPosition(0);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [typingMode, selectedWordIndex, allWords, navigateInBin, findNavigableWord, activeWordGlobalIndex, data, onTranscriptChange, cursorPosition, enableBinNavigation]);

  if (!data) {
    return (
      <div className="transcript">
        <p>No transcript data available</p>
      </div>
    );
  }

  // Allow empty transcripts to render normally
  const hasNoContent = processedSegments.length === 0;


  return (
    <>
      {alternativesLoadingCount > 0 && (
        <div className="alternatives-loading-banner">
          <div className="loading-spinner-small"></div>
          <span>Loading alternatives for {alternativesLoadingCount} of {alternativesTotalCount} low-confidence words...</span>
        </div>
      )}
      {isTranscribing && !hasNoContent && (
        <div className="retry-transcription-overlay">
          <div className="retry-loading-content">
            <div className="loading-spinner-large"></div>
            <div className="loading-text">Re-transcribing audio...</div>
            <div className="loading-subtext">This may take a few moments</div>
          </div>
        </div>
      )}
      <div className="transcript-header">
        <div className="threshold-controls">
          <div className="threshold-control">
            <label>Medium Threshold: {(mediumThreshold * 100).toFixed(1)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.001"
              value={mediumThreshold}
              onChange={(e) => onMediumThresholdChange(parseFloat(e.target.value))}
            />
          </div>
          <div className="threshold-control">
            <label>High Threshold: {(highThreshold * 100).toFixed(1)}%</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.001"
              value={highThreshold}
              onChange={(e) => onHighThresholdChange(parseFloat(e.target.value))}
            />
          </div>
        </div>

        <div className={`transcript-controls-row ${(isHelpVisible || isSettingsVisible) ? 'popup-open' : ''}`}>
          <div className="control-buttons-center">
            {showTypingModeToggle && (
              <button
                className={`toggle-button ${typingMode ? 'active' : ''}`}
                onClick={onTypingModeToggle}
                type="button"
              >
                Typing Mode
              </button>
            )}

            {showClickToDeleteToggle && (
              <button
                className={`toggle-button ${clickToDeleteMode ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  debugLog('[Transcript] Click to Delete button clicked');
                  if (onClickToDeleteModeToggle) {
                    onClickToDeleteModeToggle();
                  } else {
                    setInternalClickToDeleteMode(prev => !prev);
                  }
                }}
                type="button"
              >
                Click to Delete
              </button>
            )}

            {showSuggestionsModeToggle && (
              <button
                className={`toggle-button ${suggestionsMode ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  debugLog('[Transcript] Suggestions Mode button clicked');
                  if (onSuggestionsModeToggle) {
                    onSuggestionsModeToggle();
                  }
                }}
                type="button"
              >
                Suggestions
              </button>
            )}
          </div>

          <div className="control-buttons-right">
            {enableBinNavigation && (
              <div className="confidence-legend-compact-centered">
                <div
                  className="legend-item clickable"
                  onClick={() => navigateToConfidenceBin('high-confidence')}
                  title="Click to jump to next high-confidence word"
                >
                  <div className="color-box high-confidence"></div>
                  <span>H: {confidenceStats.high}</span>
                </div>
                <div
                  className="legend-item clickable"
                  onClick={() => navigateToConfidenceBin('medium-confidence')}
                  title="Click to jump to next medium-confidence word"
                >
                  <div className="color-box medium-confidence"></div>
                  <span>M: {confidenceStats.medium}</span>
                </div>
                <div
                  className="legend-item clickable"
                  onClick={() => navigateToConfidenceBin('low-confidence')}
                  title="Click to jump to next low-confidence word"
                >
                  <div className="color-box low-confidence"></div>
                  <span>L: {confidenceStats.low}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop top-right controls */}
        <div className="transcript-top-right-controls">
          <button type="button" className="desktop-control-btn" onClick={handleCopy} title="Copy text">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
              <rect x="2" y="2" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <rect x="6" y="6" width="8" height="8" rx="1" fill="currentColor"/>
            </svg>
          </button>
          {transcriptId && (
            <div className="export-ai-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
              <button
                type="button"
                className="desktop-control-btn"
                onClick={() => setShowSubtitleMenu(!showSubtitleMenu)}
                title="Export & AI tools"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
              </button>
              {showSubtitleMenu && (
                <div className="export-ai-menu" style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: 'var(--color-background-elevated, white)',
                  border: '1px solid var(--color-border, #e0e0e0)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  minWidth: '220px',
                  padding: '8px 0'
                }}>
                  {/* Export Transcript section */}
                  <div style={{ padding: '8px 16px', fontSize: '0.85rem', color: 'var(--color-text-secondary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Export Transcript
                  </div>
                  <button
                    className="export-menu-item"
                    onClick={() => { onDownload(); setShowSubtitleMenu(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>JSON Format</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Full transcript data</div>
                  </button>
                  <button
                    className="export-menu-item"
                    onClick={() => { onDownloadTxt(); setShowSubtitleMenu(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>Plain Text</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Simple text file</div>
                  </button>

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'var(--color-border, #e0e0e0)', margin: '8px 0' }}></div>

                  {/* Export Subtitle section */}
                  <div style={{ padding: '8px 16px', fontSize: '0.85rem', color: 'var(--color-text-secondary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Export Subtitle
                  </div>
                  <button
                    className="export-menu-item"
                    onClick={() => handleSubtitleExport('srt')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>SRT (SubRip)</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Universal format</div>
                  </button>
                  <button
                    className="export-menu-item"
                    onClick={() => handleSubtitleExport('vtt')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>VTT (WebVTT)</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Web standard, YouTube</div>
                  </button>
                  <button
                    className="export-menu-item"
                    onClick={() => handleSubtitleExport('ass')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>ASS (Advanced SubStation)</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Advanced styling</div>
                  </button>
                  <button
                    className="export-menu-item"
                    onClick={() => handleSubtitleExport('sbv')}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>SBV (YouTube Simple)</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>YouTube specific</div>
                  </button>

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'var(--color-border, #e0e0e0)', margin: '8px 0' }}></div>

                  {/* AI Tools section */}
                  <div style={{ padding: '8px 16px', fontSize: '0.85rem', color: 'var(--color-text-secondary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    AI Tools
                  </div>
                  <button
                    className="export-menu-item"
                    onClick={() => { onGenerateName && onGenerateName(); setShowSubtitleMenu(false); }}
                    disabled={!onGenerateName}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: onGenerateName ? 'pointer' : 'not-allowed',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)',
                      opacity: onGenerateName ? 1 : 0.5
                    }}
                    onMouseEnter={(e) => {
                      if (onGenerateName) {
                        e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)';
                      }
                    }}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>Generate AI Name</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>AI-generated title</div>
                  </button>
                  <button
                    className="export-menu-item"
                    onClick={() => { setShowSummaryMenu(true); setShowSubtitleMenu(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>Generate Summary</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>AI-powered summary</div>
                  </button>
                  <button
                    className="export-menu-item"
                    onClick={() => { handleGenerateFormattedVersion(); setShowSubtitleMenu(false); }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: 'var(--color-text-primary)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>Generate Formatted Version</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Clean, formatted text</div>
                  </button>
                  {onRetryTranscription && (
                    <button
                      className="export-menu-item"
                      onClick={() => { onRetryTranscription(); setShowSubtitleMenu(false); }}
                      disabled={isTranscribing}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: isTranscribing ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        color: 'var(--color-text-primary)',
                        opacity: isTranscribing ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isTranscribing) {
                          e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)';
                        }
                      }}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: '500' }}>Retry Transcription</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                        {isTranscribing ? 'Transcribing...' : 'Re-process audio'}
                      </div>
                    </button>
                  )}

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'var(--color-border, #e0e0e0)', margin: '8px 0' }}></div>

                  {/* Professional Services section */}
                  <div style={{ padding: '8px 16px', fontSize: '0.85rem', color: 'var(--color-text-secondary)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Professional Services
                  </div>
                  <button
                    className="export-menu-item"
                    onClick={() => { if (!hasPendingCorrection) { setShowCorrectionRequest(true); setShowSubtitleMenu(false); } }}
                    disabled={hasPendingCorrection}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: hasPendingCorrection ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      color: hasPendingCorrection ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                      opacity: hasPendingCorrection ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => { if (!hasPendingCorrection) e.currentTarget.style.background = 'var(--color-background-hover, #f5f5f5)'; }}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>👤</span> {hasPendingCorrection ? 'Correction In Progress' : 'Request Human Correction'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                      {hasPendingCorrection ? 'A correction request is already active' : '$5/hour - Professional review'}
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
          {onShare && (
            <button type="button" className="desktop-control-btn" onClick={onShare} title="Share transcript">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5zm-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
              </svg>
            </button>
          )}

          {/* Human Correction Request Button */}
          {transcriptId && process.env.REACT_APP_ENABLE_HUMAN_CORRECTION === 'true' && (
            <button
              type="button"
              className="desktop-control-btn"
              onClick={() => { if (!hasPendingCorrection) setShowCorrectionRequest(true); }}
              disabled={hasPendingCorrection}
              title={hasPendingCorrection ? 'Correction request already in progress' : 'Request professional human correction'}
              style={hasPendingCorrection ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </button>
          )}

          <button className="desktop-control-btn" onClick={() => setIsSettingsVisible(!isSettingsVisible)} title="Settings" type="button">
            ⚙️
          </button>
          <button className="desktop-control-btn" onClick={() => setIsHelpVisible(!isHelpVisible)} title="Help" type="button">
            ?
          </button>
        </div>

      </div>
      {/* Hidden input for mobile keyboard capture */}
      {typingMode && selectedWordIndex !== null && isMobile && (
        <input
          ref={mobileInputRef}
          type="text"
          className="mobile-hidden-input"
          value={mobileEditValue}
          onChange={handleMobileInput}
          onKeyDown={handleMobileKeyDown}
          onBlur={(e) => {
            // Prevent blur from deselecting when clicking another word
            if (!e.relatedTarget || !e.relatedTarget.classList?.contains('token')) {
              // Only keep focus if we're still in typing mode with a selection
              if (typingMode && selectedWordIndex !== null && isMobile) {
                setTimeout(() => {
                  if (mobileInputRef.current) {
                    mobileInputRef.current.focus({ preventScroll: true });
                  }
                }, 50);
              }
            }
          }}
          aria-label="Edit word"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
        />
      )}
      <div className="transcript-wrapper">
        <div
          className={`transcript-content ${typingMode ? 'typing-mode' : ''} ${data?.isLive ? 'live-mode' : ''}`}
        ref={transcriptContentRef}
        tabIndex={typingMode ? 0 : -1}
        role={typingMode ? "textbox" : "region"}
        aria-label={typingMode ? "Editable transcript" : "Transcript content"}
        data-mobile-keyboard={typingMode ? "true" : "false"}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => {
          // Close alternatives dropdown if clicking outside of it
          if (openAlternativesId) {
            // Prevent closing immediately after opening (within 300ms)
            const timeSinceOpen = Date.now() - alternativesOpenTimeRef.current;
            if (timeSinceOpen < 300) {
              return;
            }

            const clickedToken = e.target.closest('.token');
            const clickedDropdown = e.target.closest('.alternatives-dropdown');

            if (!clickedDropdown) {
              setOpenAlternativesId(null);
            }
          }

          // Exit typing mode if clicking outside of words (on background)
          const clickedToken = e.target.closest('.token');
          const clickedButton = e.target.closest('button');
          const clickedDropdown = e.target.closest('.alternatives-dropdown');

          if (typingMode && !clickedToken && !clickedButton && !clickedDropdown) {
            // Deselect word
            setSelectedWordIndex(null);
            setCursorPosition(0);
            setMobileEditValue('');

            // Exit typing mode
            if (onTypingModeToggle) {
              onTypingModeToggle();
            }
          }
        }}
      >
        {isTranscribing && hasNoContent ? (
          <div className="transcription-loading-state">
            <div className="loading-spinner-large"></div>
            <div className="loading-text">Transcribing audio...</div>
            <div className="loading-subtext">This may take a few moments</div>
          </div>
        ) : showSegments ? (
          <>
            {visibleSegments.map((segment, segmentIndex) => {
            // Use segmentIndex directly since we don't have pagination
            const actualSegmentIndex = segmentIndex;
            const segmentWords = allWords.filter(w => w.segmentIndex === actualSegmentIndex);
            // Use previous segment's end as this segment's start (except for first segment)
            const displayStart = actualSegmentIndex === 0 ? segment.start : processedSegments[actualSegmentIndex - 1].end;
            return (
              <div key={data?.isLive ? `seg-${segment.start.toFixed(2)}` : actualSegmentIndex} className="segment">
                <div className="segment-time">
                  {formatTime(displayStart)} - {formatTime(segment.end)}
                </div>
                {segment.speaker && (
                  <div className="segment-speaker">
                    <SpeakerLabel
                      key={`${segment.speaker}-${transcriptSpeakers.length}-${transcriptSpeakers.find(ts => ts.localLabel === segment.speaker)?.customLabel || ''}`}
                      speaker={segment.speaker}
                      transcriptId={transcriptId}
                      transcriptSpeakers={transcriptSpeakers}
                      availableSpeakers={availableSpeakers}
                      onSpeakerUpdated={handleSpeakerUpdated}
                    />
                  </div>
                )}
                <div className="segment-words">
                  {segmentWords.filter(word => {
                    if (hideGhosted && word.isGhosted) return false;
                    if (hideFillerWords && isFillerWord(word.word)) return false;
                    return true;
                  }).map((word, filteredIndex, filteredWords) => {
                    const globalIndex = word.globalIndex;
                    wordRefs.current[globalIndex] = wordRefs.current[globalIndex] || React.createRef();
                    const prevFilteredWord = filteredIndex > 0 ? filteredWords[filteredIndex - 1] : null;
                    const showBoundaryHandle = prevFilteredWord && !typingMode && !prevFilteredWord.isGhosted && !word.isGhosted;
                    return (
                      <React.Fragment key={`${word.segmentIndex}-${word.wordIndex}-${word.word}`}>
                        {showBoundaryHandle && (
                          <BoundaryDragHandle
                            prevWord={prevFilteredWord}
                            nextWord={word}
                            prevGlobalIndex={prevFilteredWord.globalIndex}
                            nextGlobalIndex={globalIndex}
                            onBoundaryChange={handleBoundaryChange}
                          />
                        )}
                        <Token
                          ref={wordRefs.current[globalIndex]}
                          text={word.word}
                          confidence={word.confidence ?? word.probability}
                          start={word.start}
                          end={word.end}
                          onClick={onTokenClick}
                          onWordSelect={handleWordSelect}
                          onDoubleClickEnterTyping={handleDoubleClickEnterTyping}
                          typingMode={typingMode}
                          clickToDeleteMode={clickToDeleteMode}
                          isActive={activeWordGlobalIndex === globalIndex}
                          isSelected={selectedWordIndex === globalIndex}
                          isGhosted={word.isGhosted}
                          isInserted={word.isInserted}
                          highThreshold={highThreshold}
                          mediumThreshold={mediumThreshold}
                          draggable={typingMode && word.isInserted}
                          segmentIndex={word.segmentIndex}
                          wordIndex={word.wordIndex}
                          globalIndex={globalIndex}
                          cursorPosition={typingMode && selectedWordIndex === globalIndex ? cursorPosition : null}
                          showConfidence={showConfidence}
                          alternatives={word.alternatives || []}
                          onWordReplacement={handleWordReplacement}
                          suggestionsMode={suggestionsMode}
                          openAlternativesId={openAlternativesId}
                          onAlternativesToggle={handleAlternativesToggle}
                          readOnlyAlternatives={readOnlyAlternatives}
                          previousWord={globalIndex > 0 ? allWords[globalIndex - 1]?.word : null}
                          nextWord={globalIndex < allWords.length - 1 ? allWords[globalIndex + 1]?.word : null}
                          speaker={word.speaker}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </>
        ) : (
          <div className="segment">
            <div className="segment-words">
              {allWords.filter(word => {
                if (hideGhosted && word.isGhosted) return false;
                if (hideFillerWords && isFillerWord(word.word)) return false;
                return true;
              }).map((word, filteredIndex, filteredWords) => {
                const globalIndex = word.globalIndex;
                wordRefs.current[globalIndex] = wordRefs.current[globalIndex] || React.createRef();
                const prevFilteredWord = filteredIndex > 0 ? filteredWords[filteredIndex - 1] : null;
                const showBoundaryHandle = prevFilteredWord && !typingMode && !prevFilteredWord.isGhosted && !word.isGhosted;
                return (
                  <React.Fragment key={`${word.segmentIndex}-${word.wordIndex}-${word.word}`}>
                    {showBoundaryHandle && (
                      <BoundaryDragHandle
                        prevWord={prevFilteredWord}
                        nextWord={word}
                        prevGlobalIndex={prevFilteredWord.globalIndex}
                        nextGlobalIndex={globalIndex}
                        onBoundaryChange={handleBoundaryChange}
                      />
                    )}
                    <Token
                      ref={wordRefs.current[globalIndex]}
                      text={word.word}
                      confidence={word.confidence ?? word.probability}
                      start={word.start}
                      end={word.end}
                      onClick={onTokenClick}
                      onWordSelect={handleWordSelect}
                      onDoubleClickEnterTyping={handleDoubleClickEnterTyping}
                      typingMode={typingMode}
                      clickToDeleteMode={clickToDeleteMode}
                      isActive={activeWordGlobalIndex === globalIndex}
                      isSelected={selectedWordIndex === globalIndex}
                      isGhosted={word.isGhosted}
                      isInserted={word.isInserted}
                      highThreshold={highThreshold}
                      mediumThreshold={mediumThreshold}
                      draggable={typingMode && word.isInserted}
                      segmentIndex={word.segmentIndex}
                      wordIndex={word.wordIndex}
                      globalIndex={globalIndex}
                      cursorPosition={typingMode && selectedWordIndex === globalIndex ? cursorPosition : null}
                      showConfidence={showConfidence}
                      alternatives={word.alternatives || []}
                      onWordReplacement={handleWordReplacement}
                      suggestionsMode={suggestionsMode}
                      openAlternativesId={openAlternativesId}
                      onAlternativesToggle={handleAlternativesToggle}
                      readOnlyAlternatives={readOnlyAlternatives}
                      previousWord={globalIndex > 0 ? allWords[globalIndex - 1]?.word : null}
                      nextWord={globalIndex < allWords.length - 1 ? allWords[globalIndex + 1]?.word : null}
                      speaker={word.speaker}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Typing mode suggestions dropdown */}
      {showSuggestions && typingMode && selectedWordIndex !== null && (() => {
        // Recalculate word info from current data to avoid stale indices
        const getWordInfoFromData = (data, globalIndex) => {
          if (!data || !data.segments) return null;

          let currentGlobalIndex = 0;
          for (let segIdx = 0; segIdx < data.segments.length; segIdx++) {
            const segment = data.segments[segIdx];
            if (!segment || !segment.words) continue;

            for (let wordIdx = 0; wordIdx < segment.words.length; wordIdx++) {
              if (currentGlobalIndex === globalIndex) {
                return {
                  word: segment.words[wordIdx],
                  segmentIndex: segIdx,
                  wordIndex: wordIdx,
                  globalIndex: currentGlobalIndex,
                  alternatives: segment.words[wordIdx].alternatives || []
                };
              }
              currentGlobalIndex++;
            }
          }
          return null;
        };

        const wordInfo = getWordInfoFromData(data, selectedWordIndex);
        const alternatives = wordInfo?.alternatives || [];
        const wordRef = wordRefs.current[selectedWordIndex];

        if (alternatives.length === 0 || !wordRef?.current || !wordInfo) return null;

        const tokenRect = wordRef.current.getBoundingClientRect();

        // Calculate dropdown height estimate (header + options)
        const estimatedDropdownHeight = 40 + (alternatives.length * 36); // rough estimate
        const spaceBelow = window.innerHeight - tokenRect.bottom;
        const spaceAbove = tokenRect.top;

        // Position above if not enough space below
        const shouldPositionAbove = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

        const dropdownStyle = {
          position: 'fixed',
          top: shouldPositionAbove ? `${tokenRect.top - estimatedDropdownHeight - 4}px` : `${tokenRect.bottom + 4}px`,
          left: `${tokenRect.left}px`,
          zIndex: 1000,
          maxHeight: shouldPositionAbove ? `${spaceAbove - 10}px` : `${spaceBelow - 10}px`,
          overflowY: 'auto'
        };

        return (
          <div className="typing-suggestions-dropdown" style={dropdownStyle}>
            <div className="suggestions-header">Suggestions (↑↓ to navigate, Enter to select, Tab to exit):</div>
            {alternatives.map((alt, index) => (
              <div
                key={index}
                ref={el => suggestionRefs.current[index] = el}
                className={`suggestion-option ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                onClick={() => {
                  const newData = JSON.parse(JSON.stringify(data));
                  const { segmentIndex, wordIndex } = wordInfo;
                  let wordToModify = newData.segments[segmentIndex].words[wordIndex];
                  let newGlobalIndex = selectedWordIndex;

                  if (!wordToModify.isInserted) {
                    wordToModify.isGhosted = true;
                    const newWord = {
                      word: alt.word,
                      start: null,
                      end: null,
                      confidence: null,
                      isInserted: true,
                      isGhosted: false,
                    };
                    const insertionPoint = findInsertionPointAfterGhosts(segmentIndex, wordIndex);
                    newData.segments[segmentIndex].words.splice(insertionPoint, 0, newWord);
                    const wordsBeforeInsertion = newData.segments.slice(0, segmentIndex).reduce((acc, seg) => acc + seg.words.length, 0) + insertionPoint;
                    newGlobalIndex = wordsBeforeInsertion;
                  } else {
                    wordToModify.word = alt.word;
                  }

                  setShowSuggestions(false);
                  setSelectedSuggestionIndex(0);
                  setNextSelectionInfo({ newGlobalIndex: newGlobalIndex, cursor: alt.word.length });
                  onTranscriptChange(newData);
                }}
              >
                <span className="suggestion-word">{alt.word}</span>
                <span className="suggestion-probability">{((alt.probability || 0) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        );
      })()}

      <ContextMenu
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        onClose={handleContextMenuClose}
        onCopyText={handleContextCopyText}
        onDeleteWord={handleContextDeleteWord}
        onUndeleteWord={handleContextUndeleteWord}
        onInsertWord={handleContextInsertWord}
        onEditWord={handleContextEditWord}
        onShowAlternatives={handleContextShowAlternatives}
        onPlayWord={handlePlayWord}
        onPlaySentence={handlePlaySentence}
        onPause={handlePause}
        hasSelectedText={contextMenu.hasSelectedText}
        selectedWord={contextMenu.selectedWord}
        hasAlternatives={contextMenu.selectedWord ? (() => {
          const globalIndex = contextMenu.selectedWord.index;
          const word = allWords[globalIndex];
          // Show alternatives option for all non-inserted words
          return word && !word.isInserted;
        })() : false}
        isDeleted={false}
        isGhosted={contextMenu.selectedWord ? (() => {
          const globalIndex = contextMenu.selectedWord.index;
          const word = allWords[globalIndex];
          return word && word.isGhosted;
        })() : false}
        onChangeSpeaker={handleChangeSpeaker}
        transcriptSpeakers={transcriptSpeakers}
        wordSpeaker={contextMenu.selectedWord ? (() => {
          const globalIndex = contextMenu.selectedWord.index;
          const word = allWords[globalIndex];
          return word?.speaker;
        })() : null}
        onEditTimestamp={handleEditTimestamp}
        onShiftTimestamps={handleShiftTimestamps}
        onDistributeTimestamps={handleDistributeTimestamps}
        hasMultiWordSelection={multiWordSelection !== null && multiWordSelection.startWordIndex !== multiWordSelection.endWordIndex}
        onSplitSpeaker={handleSplitSpeaker}
        selectedWordRange={multiWordSelection}
        currentWordText={contextMenu.selectedWord ? (() => {
          const globalIndex = contextMenu.selectedWord.index;
          const word = allWords[globalIndex];
          return word?.word || '';
        })() : ''}
        onReplaceWithDictionaryWord={handleReplaceWithDictionaryWord}
      />

      {/* Timestamp Editor */}
      {timestampEditor && (
        <TimestampEditor
          word={timestampEditor.word}
          wordIndex={timestampEditor.wordIndex}
          segmentIndex={timestampEditor.segmentIndex}
          globalIndex={timestampEditor.globalIndex}
          anchorElement={timestampEditor.anchorElement}
          audioRef={audioRef}
          onSave={handleTimestampEditorSave}
          onCancel={handleTimestampEditorCancel}
          previousWord={timestampEditor.previousWord}
          nextWord={timestampEditor.nextWord}
        />
      )}

      {/* Timestamp Shift Dialog */}
      {timestampShiftDialog && (
        <TimestampShiftDialog
          selectedWords={timestampShiftDialog.selectedWords}
          onApply={handleShiftDialogApply}
          onCancel={handleShiftDialogCancel}
        />
      )}

      {/* Timestamp Distribute Dialog */}
      {timestampDistributeDialog && (
        <TimestampDistributeDialog
          selectedWords={timestampDistributeDialog.selectedWords}
          onApply={handleDistributeDialogApply}
          onCancel={handleDistributeDialogCancel}
        />
      )}

      {/* Popups rendered at root level to avoid z-index stacking issues */}
      {isHelpVisible && (
        <>
          <div className="popup-overlay" onClick={() => setIsHelpVisible(false)}></div>
          <div className="popup-modal">
            <div className="popup-modal-content">
              <button className="popup-close" onClick={() => setIsHelpVisible(false)}>×</button>
              <h4>Keyboard Shortcuts</h4>
              {typingMode ? (
                <>
                  <strong>Typing Mode Controls:</strong>
                  <ul>
                    <li><strong>Arrow Keys:</strong> Move cursor within a word.</li>
                    <li><strong>Ctrl + Arrow Keys:</strong> Jump between words.</li>
                    <li><strong>Spacebar:</strong> Insert or split a word.</li>
                    <li><strong>Backspace/Delete:</strong> Delete character or ghost/delete word.</li>
                    <li><strong>Escape:</strong> Deselect word.</li>
                    <li><strong>Click Word:</strong> Select for editing.</li>
                    <li><strong>Drag & Drop:</strong> Move inserted words.</li>
                  </ul>
                </>
              ) : (
                <>
                  <strong>Listening Mode Controls:</strong>
                  <ul>
                    <li><strong>Click Word:</strong> Seek audio to that word.</li>
                    <li><strong>Arrow Keys:</strong> Navigate word by word (or bin by bin if "Enable Bin Navigation" is checked in settings).</li>
                    <li><strong>Shift + Arrow Keys:</strong> Navigate between words in the same confidence bin (or word by word if "Enable Bin Navigation" is checked).</li>
                    <li><strong>Click Confidence Levels:</strong> Click on H/M/L confidence labels to jump to the next word in that confidence bin.</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {isSettingsVisible && (
        <>
          <div className="popup-overlay" onClick={() => setIsSettingsVisible(false)}></div>
          <div className="popup-modal">
            <div className="popup-modal-content">
              <button className="popup-close" onClick={() => setIsSettingsVisible(false)}>×</button>
              <h4>Settings</h4>

              <div className="setting-group">
                <label>
                  <input
                    type="checkbox"
                    checked={showConfidenceLegend}
                    onChange={() => {
                      if (onShowConfidenceLegendToggle) {
                        onShowConfidenceLegendToggle();
                      } else {
                        setInternalShowConfidenceLegend(prev => !prev);
                      }
                    }}
                  />
                  Show Confidence Counter
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={enableBinNavigation}
                    onChange={() => {
                      if (onEnableBinNavigationToggle) {
                        onEnableBinNavigationToggle();
                      } else {
                        setInternalEnableBinNavigation(prev => !prev);
                      }
                    }}
                  />
                  Enable Bin Navigation
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={showConfidence}
                    onChange={onShowConfidenceToggle}
                  />
                  Show Confidence %
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={showSegments}
                    onChange={onShowSegmentsToggle}
                  />
                  Group into Segments
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={hideGhosted}
                    onChange={() => {
                      if (onHideGhostedToggle) {
                        onHideGhostedToggle();
                      } else {
                        setInternalHideGhosted(prev => !prev);
                      }
                    }}
                  />
                  Hide Deleted Words
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={hideFillerWords}
                    onChange={() => setHideFillerWords(prev => !prev)}
                  />
                  Hide Filler Words (um, uh, like, etc.)
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={showClickToDeleteToggle}
                    onChange={() => {
                      if (onShowClickToDeleteToggleChange) {
                        onShowClickToDeleteToggleChange();
                      } else {
                        setInternalShowClickToDeleteToggle(prev => !prev);
                      }
                    }}
                  />
                  Show Click to Delete Toggle
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={showSuggestionsModeToggle}
                    onChange={() => {
                      if (onShowSuggestionsModeToggleChange) {
                        onShowSuggestionsModeToggleChange();
                      } else {
                        setInternalShowSuggestionsModeToggle(prev => !prev);
                      }
                    }}
                  />
                  Show Suggestions Mode Toggle
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={silenceCompressionMode}
                    onChange={() => {
                      const newValue = !silenceCompressionMode;
                      setSilenceCompressionMode(newValue);
                      // Disable corrections mode if enabling silence compression
                      if (newValue && correctionsPlaybackMode) {
                        setCorrectionsPlaybackMode(false);
                      }
                    }}
                  />
                  Skip Silence During Playback (&gt; 0.25s)
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={correctionsPlaybackMode}
                    onChange={() => {
                      const newValue = !correctionsPlaybackMode;
                      setCorrectionsPlaybackMode(newValue);
                      // Disable silence compression if enabling corrections mode
                      if (newValue && silenceCompressionMode) {
                        setSilenceCompressionMode(false);
                      }
                    }}
                  />
                  Review Edits Only Mode
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={enableDiarization}
                    onChange={() => {
                      if (onEnableDiarizationToggle) {
                        onEnableDiarizationToggle();
                      } else {
                        setInternalEnableDiarization(prev => !prev);
                      }
                    }}
                  />
                  Enable Speaker Diarization
                </label>
              </div>

              {showSegments && (
                <div className="setting-group" style={{ marginTop: '1rem' }}>
                  <h5>Segment Duration</h5>
                  <label>
                    Minimum Segment Length: {segmentDuration < 60 ? `${segmentDuration}s` : `${Math.floor(segmentDuration / 60)}m ${segmentDuration % 60}s`}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={(() => {
                      // Convert duration to slider position (inverse of the formula below)
                      // duration = 5 + (sliderValue/100)^2 * 295
                      // (duration - 5) / 295 = (sliderValue/100)^2
                      // sqrt((duration - 5) / 295) = sliderValue/100
                      // sliderValue = 100 * sqrt((duration - 5) / 295)
                      return Math.round(100 * Math.sqrt(Math.max(0, segmentDuration - 5) / 295));
                    })()}
                    onChange={(e) => {
                      const sliderValue = parseInt(e.target.value);
                      // Non-linear scale: more granularity at lower values, compressed at higher values
                      // Formula: duration = 5 + (sliderValue/100)^2 * 295
                      // This gives: 0→5s, 25→23s, 50→79s, 75→171s, 100→300s
                      const duration = Math.round(5 + Math.pow(sliderValue / 100, 2) * 295);
                      onSegmentDurationChange(duration);
                    }}
                    style={{ width: '100%' }}
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                    Range: 5s to 5min
                  </small>
                </div>
              )}

              <div className="threshold-controls-compact">
                <h5>Confidence Thresholds</h5>
                <div className="threshold-control">
                  <label>Medium: {(mediumThreshold * 100).toFixed(0)}%</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={mediumThreshold}
                    onChange={(e) => onMediumThresholdChange(parseFloat(e.target.value))}
                  />
                </div>
                <div className="threshold-control">
                  <label>High: {(highThreshold * 100).toFixed(0)}%</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={highThreshold}
                    onChange={(e) => onHighThresholdChange(parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Summary Menu */}
      {showSummaryMenu && transcriptId && (
        <SummaryMenu
          transcriptId={transcriptId}
          onClose={() => setShowSummaryMenu(false)}
          onSeekToTime={onTokenClick}
        />
      )}

      {/* Human Correction Request Modal */}
      {showCorrectionRequest && transcriptId && (
        <CorrectionRequest
          transcriptId={transcriptId}
          transcriptData={data}
          audioDuration={audioRef?.current?.duration || 0}
          onClose={() => setShowCorrectionRequest(false)}
          onSuccess={(requestId) => {
            debugLog('Correction request submitted:', requestId);
            setHasPendingCorrection(true);
          }}
        />
      )}
    </>
  );
};

const formatTime = (seconds) => {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default Transcript;
